/**
 * @fileoverview Main application orchestrator for CasebookMap.
 * Coordinates map initialization, configuration loading, searchers, and state persistence.
 * @module app/MapApp
 */


import { ConfigLoader } from '../config/ConfigLoader.js';
import { OpenLayersMapManager } from '../map/OpenLayersMapManager.js';
import { StateManager } from '../state/StateManager.js';
import { PoiDataManager } from '../data/PoiDataManager.js';
import { AuxDataManager } from '../data/AuxDataManager.js';
import { UiManager } from '../ui/UiManager.js';

import { WhiteSearcher } from '../searchers/WhiteSearcher.js';
import { YellowSearcher } from '../searchers/YellowSearcher.js';
import { ReverseSearcher } from '../searchers/ReverseSearcher.js';
import { AdvancedSearcher } from '../searchers/AdvancedSearcher.js';

import { HistorySearcher } from '../searchers/HistorySearcher.js';
import { BookmarkSearcher } from '../searchers/BookmarkSearcher.js';
import { SavedSearcher } from '../searchers/SavedSearcher.js';

import { fixRelativeChildPathWithParentFilePath, switchToBootstrapTabById, appendCacheBuster, buildSearchPackFromQuery, escapeHtml, showStatusWarning, showStatusHelpful, setupResponsiveText, deepCopy, normalizeStaticBasePath, getOwnValue  } from '../utils/jrfuncs.js';
import { transformGeometry, distanceSquaredToCoords, distanceBetweenTwoItems, convertDistanceGeometryUnitsToNiceString, isPointInPolygonGeometry, itemHasPointGeometry} from '../utils/jrgeofuncs.js';

import {DEBUG_CBM, DEBUG_REFRESH, PTYPES, SEARCH_TYPES, DEFAULT_APP_STORAGE_PREFIX, MAPAPP_DEFAULTS, AUTO_ZOOM_STYLE, MAP_AUX_OVERLAY_LAYERS, DOM_ELEMENTS, HELP_HTML_URL, CUSTOM_ITEM_FIELDS_ALL, MAP_CONSTANTS, THEMES, PTYPES_POIDB, CUSTOM_ITEM_FIELDS_BOOKMARK, FIELD_ENABLE_CUSTOM_APPEARANCE, FIELDS_CUSTOMIZE_IGNORE_ON_DISABLED_CUSTOM_APPEARANCE, IMAGE_MAP_PATH, USER_ICON_PICKER_IMAGELIST, WEB_URLS, NYC_LONLAT_BOUNDS, MAP_MODES, CUSTOM_ITEM_FIELDS_ALL_PLUSTEMPS, PLAYLOG_CONSTANTS, LEADS_SECTION_LABELS } from '../constants.js'

const { ID_SIDEBAR, ID_SIDEBAR_TITLE, ID_HELP_VIEWER_CONTAINER, ID_APP_CONTAINER, ID_SEARCH_TABS, CLASS_LOADING, CLASS_LOADED, ID_MAIN_PANEL_TABS, ID_MAIN_PANEL_CONTENT, ID_MAIN_MAP_TAB, ID_MAIN_MAP_PANE, ID_MAIN_READING_TAB, ID_MAIN_READING_TAB_CLOSE, ID_READING_PANE, ID_MAIN_BGMUSIC_TAB, ID_MAIN_BGMUSIC_TAB_CLOSE, ID_MAIN_BGMUSIC_PANE } = DOM_ELEMENTS;

import { HelpViewer } from '../ui/HelpViewer.js';
import { SettingsViewer } from '../ui/SettingsViewer.js';
import { ImportExportHelper } from '../helpers/ImportExportHelper.js';
import { LogHelper } from '../helpers/LogHelper.js';
import { ImageHelper } from '../helpers/ImageHelper.js';
import { PlayLogger } from '../helpers/PlayLogger.js';
import { BookmarkDialog } from '../ui/BookmarkDialog.js';
import { WorldSelectDialog } from '../ui/WorldSelectDialog.js';
import { InformationDialog } from '../ui/InformationDialog.js';
import { PdfViewer } from '../ui/PdfViewer.js';
import { BackgroundMusicPlayer } from '../ui/BackgroundMusicPlayer.js';

import { parseBlockQueryString } from '../utils/nynfuncs.js';
import { getDefaultLeadIdPatterner } from '../utils/LeadIdPatterner.js';


/**
 * Main application class for CasebookMap.
 * Orchestrates map initialization, configuration loading, layer management,
 * searchers, and state persistence.
 * @class
 */
export class MapApp {
    /**
     * Creates a new MapApp instance.
     * @param {string} containerId - DOM element ID for the map container
     */
    constructor(containerId, options = {}) {
        this.containerId = containerId;
        // Static base path for resolving relative asset URLs (e.g., '/static/' for Django). Normalized to a
        // clean directory path (no query string/fragment, trailing slash), so resolveStaticPath() joins
        // files onto it as a path: a "?" or "#" in it can no longer make every asset url the same file.
        this.staticBasePath = normalizeStaticBasePath(options.staticBasePath || '');
        this.appKeyStore = options.appKeyStore || '';
        this.worldPathStartup = options.worldPath || null;
        this.worldIdStartup = options.worldId || null;
        // optional startup pdf url (from ?pdfUrl=...) to auto-load into the reading pane
        this.pdfUrlStartup = options.pdfUrl || null;

        this.stateManager = new StateManager(this, DEFAULT_APP_STORAGE_PREFIX);
        this.configLoader = new ConfigLoader(this);
        this.importExportHelper = new ImportExportHelper(this);
        this.logHelper = new LogHelper(this);
        this.imageHelper = new ImageHelper(this);
        this.playLogger = new PlayLogger(this);

        // The sidebar chrome (collapse arrows, menu, search tabs, resize handle) is static page DOM, so
        // its manager is app-scoped too: constructed once here, and initializeUiManager() re-applies only
        // its per-world state on each world load. It used to be re-created per world, which stacked one
        // click handler per load on the same buttons — an even stack toggled twice and looked dead.
        this.uiManager = new UiManager(this);

        // Same rule for the other listeners on static DOM that a world load installs: bound once per page,
        // guarded by these flags (a world switch would otherwise fire each handler once per load).
        this.globalHotkeysInstalled = false;
        // whether the user last pressed or focused something in the sidebar's search panes (kept by
        // setupGlobalHotkeys, read by routeUnfocusedKeyToPdfReader)
        this.lastInteractionInSidebarPanes = false;
        this.searchBarMenuListenersInstalled = false;
        this.sidebarErrorListenerInstalled = false;

        // The Background Music player is app-scoped (its playlists come from StateManager, not the world),
        // so it persists across world switches and is NOT reset by resetOwnedStuff(); init it once here.
        this.bgMusicPlayer = null;

        // The Help, Settings and PDF viewers are app-scoped too, for the same static-DOM reason as the
        // sidebar: each binds listeners to page DOM that exists once (the settings modal's Save button
        // and hidden event, the help container's click delegation, the reading pane's resize observer
        // and document listeners). They used to be nulled on every world load and rebuilt on next use,
        // which stacked one more set of those handlers per world switch -- and dropped the PDF the user
        // was reading, since the next getPdfViewer() built a fresh viewer over the pane. Created lazily
        // (getOrCreateHelpViewer / getOrCreateSettingsViewer / getPdfViewer), never reset.
        this.helpViewer = null;
        this.helpViewerCreation = null;
        this.settingsViewer = null;
        this.pdfViewer = null;

        // close function of the open extra-actions dropdown, if any (see showExtraActionsDropdown)
        this.closeExtraActionsDropdown = null;

        // True while a world is being loaded; a second world switch is refused until it finishes
        // (two interleaved loads would destroy and rebuild the map manager under each other).
        this.worldLoadInProgress = false;

        this.resetOwnedStuff();
    }

    /**
     * Resets all owned instance properties to their initial state.
     * Creates a fresh OpenLayersMapManager and clears world config, data managers,
     * viewers, region selection, anchor state, and delayed timers.
     */
    resetOwnedStuff() {
        this.mapManager = new OpenLayersMapManager(this);

        this.worldId = null;
        this.worldConfig = null;
        this.worldCacheBusterVersion = null;


        this.poiDataManager = null;
        this.auxDataManager = null;
        this.specialYellowData = null;
        this.initialized = false;

        // The searchers belong to one world: clear them here, so that after a world is torn down (and
        // above all after the next world's config fails to load) nothing can still reach -- and iterate
        // -- the destroyed searchers of the previous world. constructSearchers() fills these in again.
        this.searchersByName = {};
        this.whiteSearcher = null;
        this.yellowSearcher = null;
        this.reverseSearcher = null;
        this.advancedSearcher = null;
        this.bookmarkSearcher = null;
        this.historySearcher = null;
        this.savedSearcher = null;

        // True only once a world has fully loaded (map, searchers, data); see isWorldReady(). Unlike
        // `initialized` it stays false when a world config fails to load, so the entry points that the
        // app-scoped UI (pdf viewer, help links, settings, menus) can reach while there is no world
        // check it and do nothing instead of touching a missing map or searcher.
        this.worldReady = false;
        //
        // NOTE: bgMusicPlayer, helpViewer, settingsViewer and pdfViewer are intentionally NOT reset here —
        // they are app-scoped and must survive world switches (see the constructor: they bind static page
        // DOM, and the music keeps playing / the PDF stays open). They are created once, lazily.

        //
        this.selectedRegion = '';
        //
        // Current anchor - a single POI that can be marked with a persistent visual ring
        this.currentAnchor = { uid: null, xy: null, item: null };

        // Delayed live search save timer
        this.delayedSearchSaveTimeoutId = null;
        // Debounced map-view save timer (setupStateListeners); cleared in destroy()
        this.viewSaveTimeoutId = null;

        //for (let i = 1; i <= 100; i++) {
        //    this.addMessage(`Test message ${i}.`);
        //}
    }


    /**
     * Resolves a relative asset path using the configured staticBasePath.
     * For standalone mode (no staticBasePath), returns the path unchanged.
     * For Django mode, prepends the static URL prefix.
     * @param {string} relativePath - Relative path to an asset (e.g., 'images/mapPin.svg')
     * @returns {string} Resolved path with staticBasePath prepended if configured
     */
    resolveStaticPath(relativePath) {
        if (!this.staticBasePath || relativePath.startsWith('http') || relativePath.startsWith('/')) {
            return relativePath;
        }
        return this.staticBasePath + relativePath;
    }











    // ==========================================
    // SETUP AND INITIALIZATION
    // ==========================================



    /**
     * Initializes the application with the given configuration.
     * Loads config, initializes map, registers tile/POI layers, loads data sources,
     * and restores saved state.
     * @async
     * @param {string} configPath - Path to the JSON configuration file
     * @returns {Promise<boolean>} True if initialization succeeds
     * @throws {Error} If initialization fails at any step
     */
    async initialize(configPath) {
        try {
            // Guard against browser scroll-on-focus shifting the viewport.
            // scrollIntoView() and focus() can cause the browser to scroll
            // ancestor elements (including body) even with overflow:hidden.
            // Since this app is a single-viewport layout that should never
            // scroll at the body level, we reset any accidental scroll.
            this.installBodyScrollGuard();

            // Load and validate configuration
            this.appConfig = await this.configLoader.loadAppConfig(configPath);
            // (its appCacheBusterVersion is read where it is used: ConfigLoader.loadWorldConfigFromPath)

            if (this.worldPathStartup) {
                // console.log(`ATTN: trying to load world by path from "${this.worldPathStartup}"`)
                await this.loadWorldByPath(this.worldPathStartup);
            } else {
                // Load last used world (or default) or explicit one into worldConfig
                let worldId = this.worldIdStartup;
                if (worldId && !ConfigLoader.findWorldDictById(this.appConfig, worldId)) {
                    // a ?worldId= that names no world of this config (a stale link, a typo, or a
                    // prototype name like "constructor") falls back like no parameter at all
                    const message = `Ignored the ?worldId= parameter "${worldId}": this map has no such world.`;
                    console.warn(`CasebookMap: ${message}`);
                    this.addWarning(message);
                    showStatusWarning(message);
                    worldId = null;
                }
                if (!worldId) {
                    worldId = this.getStartupWorldId(this.appConfig);
                }
                await this.loadWorldById(worldId);
            }

            // If a startup pdf url (?pdfUrl=...) was passed, load it into the reading pane now that
            // the world (and thus the main panel tabs / pdf viewer) is fully initialized.
            if (this.pdfUrlStartup && this.initialized) {
                await this.loadStartupPdf(this.pdfUrlStartup);
            }
        } catch (error) {
            let message = `ERROR: Failed to initialize MapApp(1): ${error}`;
            this.addError(message);
            throw error;
        }
    }




    /**
     * Loads and initializes a world configuration.
     * Sets up storage, map, managers, tile layers, data sources, controls, and state.
     * @async
     * @param {Object} worldConfig - World configuration object containing map, tile, and data settings
     * @returns {Promise<boolean>} True if initialization succeeds
     * @throws {Error} If initialization fails at any step
     */
    async loadAndInitializeWorld(worldConfig) {
        try {
            // first sidebar which can show errors
            if (worldConfig==false) {
                // initialize menus so user can load another world, etc.
                this.initializeUiManager();
                this.initialized = true;
                // Update sidebar title with world label
                this.updateSidebarTitle();
                // Update main panel tabs
                this.initializeMainPanelTabs()
                return false;
            } else {
                this.initSidebarError();
            }

            // Store cache buster version from world config (if present)
            this.worldCacheBusterVersion = worldConfig.cacheBusterVersion || null;

            // initialize storage manager with the world key
            this.stateManager.initializeWithWorldStoragePrefix(worldConfig.storageKeyPrefix, this.appKeyStore);

            // Initialize raster projection if world uses raster (pixel coordinate) mode
            if (worldConfig.mapMode === MAP_MODES.RASTER && worldConfig.rasterConfig) {
                this.mapManager.initializeRasterProjection(
                    worldConfig.rasterConfig.imageWidth,
                    worldConfig.rasterConfig.imageHeight,
                    worldConfig.mapConfig?.maxViewZoom
                );
                // Auto-calculate center from image dimensions if not explicitly provided
                if (!worldConfig.mapConfig.center) {
                    worldConfig.mapConfig.center = [
                        worldConfig.rasterConfig.imageWidth / 2,
                        -worldConfig.rasterConfig.imageHeight / 2
                    ];
                }
            }

            // Build map config with saved state to avoid visual jump on load
            const mapConfig = this.buildMapConfigWithSavedState(worldConfig.mapConfig);

            // Initialize the map with merged config (uses saved view if available)
            let retv = this.mapManager.initialize(this.containerId, mapConfig);
    
            // initialize the searchers
            await this.constructSearchers();

            // Create all result layers on the map (searcher layers + auxiliary layers)
            this.initializeResultLayers();

            // Load tile layers
            await this.loadTileLayers();

            // Load icon mappings and image lists from JSON
            await this.initializeImageHelper();

            // Initialize and load data manager
            await this.loadPoiDataSources(worldConfig);

            // Initialize and load other data
            await this.loadAuxDataSources();

            // Initialize reset view control
            this.mapManager.initializeResetViewControl();

            // Initialize fit extent control
            this.mapManager.initializeFitExtentControl();

            // Initialize overlay control with saved state
            const overlayState = this.stateManager.getAllOverlayVisibility();
            this.mapManager.initializeOverlayControl(overlayState);

            // Initialize region control with saved region
            const regionData = this.auxDataManager.getRegionData();

            // ATTN: JR - I am disabling restoration of selected region as it seems not important.
            //const savedRegion = this.stateManager.getSelectedRegion();
            // '' = no region, the value the stored state's map.selectedRegion holds for it (null here
            // used to be stored as is, and the load-time state check then reported it on every load)
            const savedRegion = '';
            //
            this.mapManager.initializeRegionControl(regionData, savedRegion);
            this.setSelectedRegionProgrammatically(savedRegion, false);

            // Initialize map effect control with saved effect
            const savedEffect = this.stateManager.getMapEffect();
            this.mapManager.initializeMapEffectControl(savedEffect);

            // Initialize info button control at bottom-right of map
            this.mapManager.initializeInfoButtonControl(() => {
                this.showWorldInformation();
            });

            // Initialize PDF viewer toggle control at upper-left of map
            this.mapManager.initializePdfViewerControl(() => {
                this.togglePdfViewer();
            });

            // Initialize zoom level display
            this.mapManager.initializeZoomLevelControl();

            // Wire dropdown controls so opening one closes the others
            this.mapManager.wireDropdownControlSiblings();

            // Restore saved state after everything is initialized
            this.restoreState();

            // configure the map style data
            this.setMapStyleData();

            // Apply theme from saved settings
            this.applyTheme();

            // Set up listeners to save state on changes
            this.setupStateListeners();

            // sidebar helpers etc.
            this.initializeUiManager();

            // Install global hotkeys (Ctrl+Q toggles PDF panel, etc.)
            this.setupGlobalHotkeys();

            // Initialize play logger: its listeners once per page, then THIS world's saved log (the
            // log is stored per world)
            this.playLogger.initialize();
            this.playLogger.loadForCurrentWorld();

            // wire up search bar menu action handlers
            this.initializeSearchBarMenuActionListeners();

            this.initialized = true;
            this.worldReady = true;

            // Reveal the map now that state is fully restored
            this.revealMap();

            // Update sidebar title with world label
            this.updateSidebarTitle();

            // Update main panel tabs
            this.initializeMainPanelTabs()

            return true;
        } catch (error) {
            let message = `ERROR: Failed to startup MapApp(2): ${error}`;
            this.addError(message);
            // the sidebar menus (pick another world, settings, help) are wired near the END of a
            // successful load; a world that throws part-way must still leave them working, as a
            // world whose config failed to load does (the worldConfig==false branch above), or the
            // user has no way out short of clearing site data
            this.recoverMenusAfterFailedWorldLoad();
            throw error;
        }
    }


    /**
     * After a world threw while initializing: wire the page's sidebar menus (they bind once per
     * page) so another world can be picked, as the failed-config branch does. Best effort -- a
     * failure here is logged and must not hide the error that got us here.
     * @private
     */
    recoverMenusAfterFailedWorldLoad() {
        try {
            this.initializeUiManager();
            this.initialized = true;
            this.updateSidebarTitle();
            this.initializeMainPanelTabs();
        } catch (recoveryError) {
            console.error('Could not set up the menus after a failed world load:', recoveryError);
        }
    }




    /**
     * Loads a world by its ID, clearing the previous world if one is loaded.
     * Fetches the world config, initializes the world, restores searcher state,
     * and triggers initial search display.
     * @async
     * @param {string} worldId - The ID of the world to load
     */
    async loadWorldById(worldId) {
        // check the id first: throws (before anything is torn down) if it is not in the app config
        const appConfigWorldPart = this.configLoader.getWorldDictById(this.appConfig, worldId);
        // console.log(`CasebookMap Loading world file ${appConfigWorldPart.label} from ${appConfigWorldPart.worldConfigPath}...`)
        return await this.loadWorldWithConfigFetcher(worldId, () => this.configLoader.loadWorldById(this.appConfig, worldId));
    }


    /**
     * Loads a world from an explicit world config path, clearing the previous world if one is loaded.
     * Fetches the world config, initializes the world, restores searcher state,
     * and triggers initial search display.
     * @async
     * @param {string} worldConfigPath - Path of the world config file to load
     */
    async loadWorldByPath(worldConfigPath) {
        const worldId = 'bypath';
        return await this.loadWorldWithConfigFetcher(worldId, () => this.configLoader.loadWorldConfigFromPath(this.appConfig, worldConfigPath, worldId));
    }


    /**
     * The one world-load sequence shared by loadWorldById and loadWorldByPath: clears the current world,
     * fetches the new world config, initializes the world, then restores searcher state and the initial
     * searches. Only one load runs at a time (a second request while one is in flight is refused, since
     * both would tear down and rebuild the same map manager). A world config that fails to load leaves
     * the app with its menus initialized (so another world can be picked) and stops there.
     * @async
     * @param {string} worldId - The world id to record for the loaded world
     * @param {Function} fetchWorldConfig - async () => worldConfig object, or false if it failed to load
     * @returns {Promise<boolean>} True if the world loaded, false if refused or the config failed to load
     * @private
     */
    async loadWorldWithConfigFetcher(worldId, fetchWorldConfig) {
        if (this.refuseIfWorldLoadInProgress()) {
            return false;
        }
        this.worldLoadInProgress = true;
        try {
            if (this.worldConfig) {
                this.clearAndEmptyCurrentWorld();
            }
            this.worldConfig = await fetchWorldConfig();
            this.worldId = worldId;
            // now initialize the world
            const loaded = await this.loadAndInitializeWorld(this.worldConfig);
            if (!loaded) {
                // the world config failed to load (already reported); there are no searchers or map
                // for this world, so the post-load steps below must not run
                return false;
            }
            // remember this world for next startup -- only once it has loaded: a world that fails
            // to load (or throws while initializing) must not be the one every reload tries again
            this.stateManager.setLastWorldId(worldId);
            // merge this world's music playlists into the user's known list (re-merges every load, so a
            // world's playlists re-appear even if the user previously removed them)
            this._mergeWorldMusicPlaylists();
            // searchers on restore state/optins
            await this.initSearchersAfterStateLoaded();
            // initial refresh of search tabs after state restored
            this.clearInitialSearches();
            // the app-scoped settings viewer may have been built before any marker images were loaded
            // (e.g. opened while the first world failed to load): fill its marker picker in now
            if (this.settingsViewer) {
                this.settingsViewer.refreshMarkerStylePickerIfEmpty();
            }
            return true;
        } finally {
            this.worldLoadInProgress = false;
        }
    }


    /**
     * If a world load is in flight, tells the user to wait and returns true.
     * @returns {boolean} True if a world load is in progress (and the caller should do nothing)
     * @private
     */
    refuseIfWorldLoadInProgress() {
        if (!this.worldLoadInProgress) {
            return false;
        }
        const message = 'A world is still loading; please wait for it to finish before switching worlds.';
        this.addWarning(message);
        showStatusWarning(message);
        return true;
    }


    /**
     * Clears the current world and loads a new one by its ID.
     * @async
     * @param {string} selectedWorldId - The ID of the new world to load
     */
    async clearWorldLoadNewWorldFileById(selectedWorldId) {
        await this.loadWorldById(selectedWorldId);
    }

    /**
     * Destroys the current world and resets all owned state.
     * Calls destroy() to clean up resources, then resetOwnedStuff() to reinitialize properties.
     */
    clearAndEmptyCurrentWorld() {
        // a play-log save still waiting belongs to the world being left: write it while the state
        // manager still points there, and empty the log pane (PlayLogger.leaveCurrentWorld)
        this.playLogger?.leaveCurrentWorld();
        this.destroy();
        this.resetOwnedStuff();
    }

    /**
     * Gets the default world ID from the app configuration.
     * @param {Object} appConfig - The app configuration object
     * @returns {string} The default world ID
     */
    getDefaultWorldId(appConfig) {
        return appConfig.defaultWorldId;
    }

    /**
     * Gets the world ID to use at startup.
     * Returns the last loaded world ID if saved and valid, otherwise the default.
     * @param {Object} appConfig - The app configuration object
     * @returns {string} The world ID to load at startup
     */
    getStartupWorldId(appConfig) {
        // own keys only (ConfigLoader.findWorldDictById): a stored "constructor" or "__proto__" used to
        // resolve to an Object.prototype member and fail the startup on every load; anything that is
        // not a world of this config now falls back to the default world, whose load then overwrites
        // the bad stored value
        const lastWorldId = this.stateManager.getLastWorldId();
        if (lastWorldId && ConfigLoader.findWorldDictById(appConfig, lastWorldId)) {
            return lastWorldId;
        }
        return this.getDefaultWorldId(appConfig);
    }






    /**
     * Initializes all searchers.
     * Creates WhiteSearcher, YellowSearcher, ReverseSearcher, AdvancedSearcher
     * for data searches, and BookmarkSearcher, HistorySearcher, SavedSearcher for user data.
     * Populates searchersByName lookup table.
     * @async
     * @private
     */
    async constructSearchers() {
        // Db searchers - each has its own class derived from DbSearcher
        this.whiteSearcher = new WhiteSearcher(this);
        this.yellowSearcher = new YellowSearcher(this);
        this.reverseSearcher = new ReverseSearcher(this);
        this.advancedSearcher = new AdvancedSearcher(this);

        // Internal Searchers - use centralized configs from IntSearcherConfigs
        this.bookmarkSearcher = new BookmarkSearcher(this);
        this.historySearcher = new HistorySearcher(this,);
        this.savedSearcher = new SavedSearcher(this);

        // Manager lookup table for getSearcherByName()
        // ATTN: these should match TAB names in index.html to support proper invoking of menu actions
        this.searchersByName = {
            white: this.whiteSearcher,
            yellow: this.yellowSearcher,
            reverse: this.reverseSearcher,
            advanced: this.advancedSearcher,
            bookmarks: this.bookmarkSearcher,
            history: this.historySearcher,
            savedsearch: this.savedSearcher,
        };

    }


    /**
     * Calls initAfterStateLoaded() on all registered searchers.
     * Invoked after state restoration so each searcher can perform
     * post-state-load initialization (e.g., restoring UI options from saved state).
     * @async
     */
    async initSearchersAfterStateLoaded() {
        if (!this.searchersByName) {
            return;
        }
        for (const searcher of Object.values(this.searchersByName)) {
            searcher.initAfterStateLoaded();
        }
    }


    /**
     * Creates all result layers on the map during initialization.
     * Searcher result layers are created from searcher configs, and
     * auxiliary layers (highlight, anchor) are created by the map manager.
     * @private
     */
    initializeResultLayers() {
        // Build layer configs from searchers
        const searcherLayerConfigs = [];
        for (const searcher of Object.values(this.searchersByName)) {
            if (searcher.mapLayerName) {
                searcherLayerConfigs.push({
                    layerName: searcher.mapLayerName,
                    options: { visible: true, zIndex: searcher.zIndexLayer }
                });
            }
        }
        this.mapManager.createAllResultLayers(searcherLayerConfigs);

        // Auxiliary layers (highlight, anchor, anchorPoi) — styles defined in map manager
        this.mapManager.createAuxResultLayers();
    }


    /**
     * (Re)initializes the UI manager for the current world: binds the sidebar's DOM listeners on the
     * first call only and re-applies the per-world sidebar state every call (see UiManager.initialize).
     * The manager itself is created once in the constructor.
     * @private
     */
    initializeUiManager() {
        this.uiManager.initialize();
    }


    /**
    * initial setting of configuration data for styles
    */
    setMapStyleData() {
        this.mapManager.setMarkerStyleConfig(this.getImageHelper().getImageListUnderKey('markers'));
        this.mapManager.setMarkerStyleId('yellow', MAPAPP_DEFAULTS.DEFAULT_MARKER_STYLE);
    }



    /**
     * Reveals the map container after initialization is complete.
     * Removes the 'loading' class that hides the map during startup.
     * @private
     */
    revealMap() {
        const container = document.getElementById(this.containerId);
        if (container) {
            container.classList.remove(CLASS_LOADING);
            container.classList.add(CLASS_LOADED);
        }
    }


    /**
     * Updates the sidebar title with the world config label.
     * Automatically switches between label and shortLabel based on available width.
     * @private
     */
    updateSidebarTitle() {
        if (!this.worldConfig) {
            return;
        }
        if (this.titleResizeObserver) {
            this.titleResizeObserver.disconnect();
        }
        if (this.worldConfig.labelShort) {
            this.titleResizeObserver = setupResponsiveText(
                ID_SIDEBAR_TITLE,
                this.worldConfig.label,
                this.worldConfig.labelShort
            );
        } else {
            const titleElement = document.getElementById(ID_SIDEBAR_TITLE);
            if (titleElement) {
                titleElement.textContent = this.worldConfig.label;
            }
        }
    }


    /**
     * Triggers initial display refresh for bookmark, history, and saved search tabs.
     * Called after state restoration to populate the searchers UIs.
     * @private
     */
    clearInitialSearches() {
        if (!this.bookmarkSearcher) {
            return;
        }
        this.bookmarkSearcher.performClearSearch(false);
        this.historySearcher.performClearSearch(false);
        this.savedSearcher.performClearSearch(false);

        // now we can refresh layer visibilities?
        this.updateOverlayLayersVisibility();
    }





    /**
     * Destroys the application and cleans up resources.
     * Calls destroy on the map instance and resets initialized flag.
     */
    destroy() {

        // map manager
        if (this.mapManager) {
            this.mapManager.destroy();
            this.mapManager = null;
        }

        // cancel any delayed timer
        this.cancelDelayedSearchTimer();
        clearTimeout(this.viewSaveTimeoutId);
        this.viewSaveTimeoutId = null;

        // iterate all searchers
        this.forEachSearcher(searcher => searcher.destroy());

        this.worldConfig = null;
        this.worldId = null;
        //
        this.initialized = false;
    }



    /**
     * Cancels all pending delayed search save timer
     */
    cancelDelayedSearchTimer() {
        this.clearPendingDelayedSearchSave();
    }




    // ==========================================
    // ACCESSORS
    // ==========================================

    /**
     * Returns whether the application has been fully initialized.
     * @returns {boolean} True if the app is initialized, false otherwise
     */
    isInitialized() {
        if (!this.initialized) {
            return false;
        }
        return true;
    }

    /**
     * Returns whether a world is fully loaded (map, searchers and data all present). False while a
     * world loads and after a world config failed to load, even though `initialized` is then true.
     * @returns {boolean} True if a world is loaded and usable
     */
    isWorldReady() {
        return this.worldReady === true;
    }


    /**
     * Gets the cache buster version from world config.
     * Used for appending to data/tile URLs to force cache refresh.
     * @returns {string|null} Cache buster version, or null if not configured
     */
    getWorldCacheBusterVersion() {
        return this.worldCacheBusterVersion;
    }


    /**
     * Gets the APP config's cache buster version (appCacheBusterVersion). It versions what every world
     * shares -- the world config fetches and the map's own data under the static base (iconMap.json,
     * the image lists; see ImageHelper) -- because mapDeployTools' bumpMapCacheVersions bumps it on
     * every run, while it bumps only some worlds' own versions: a world-versioned url of a shared file
     * stayed cached (nginx serves /static/ for 30 days) on every world that was not bumped.
     * @returns {string|null} Cache buster version, or null if not configured
     */
    getAppCacheBusterVersion() {
        return (this.appConfig && this.appConfig.appCacheBusterVersion) || null;
    }


    /**
     * Gets the existing HelpViewer instance, or creates one if it doesn't exist.
     * @returns {Promise<HelpViewer>} The HelpViewer instance
     */
    async getOrCreateHelpViewer() {
        if (!this.helpViewer) {
            // one creation at a time: two quick clicks while help.html is being fetched must not build
            // two viewers (each binds its click delegation to the same static container)
            if (!this.helpViewerCreation) {
                this.helpViewerCreation = (async () => {
                    try {
                        const helpUrl = this.resolveStaticPath(HELP_HTML_URL);
                        const helpConfig = await HelpViewer.loadConfigFromHtml(helpUrl);
                        this.helpViewer = new HelpViewer(this, ID_HELP_VIEWER_CONTAINER, helpConfig);
                    } catch (error) {
                        console.error('ERROR: Failed to load help configuration:', error);
                        const container = document.getElementById(ID_HELP_VIEWER_CONTAINER);
                        if (container) {
                            container.innerHTML =
                                '<p class="text-muted">Failed to load help content. Please try again.</p>';
                        }
                        throw error;
                    } finally {
                        this.helpViewerCreation = null;
                    }
                })();
            }
            await this.helpViewerCreation;
        }
        return this.helpViewer;
    }

    /**
     * Shows the help dialog, optionally navigating to a specific topic.
     * @param {string|null} [topicId=null] - Topic ID to display (e.g., 'about', 'search')
     * @param {string|null} [anchor=null] - Anchor within the topic to scroll to
     */
    async showHelpDialogAtTopic(topicId = null, anchor = null) {
        const helpViewer = await this.getOrCreateHelpViewer();
        helpViewer.showModal(topicId, anchor);
    }

    /**
     * Gets the existing SettingsViewer instance, or creates one if it doesn't exist.
     * @returns {SettingsViewer} The SettingsViewer instance
     */
    getOrCreateSettingsViewer() {
        if (!this.settingsViewer) {
            this.settingsViewer = new SettingsViewer(this, 'settingsViewerContainer');
        }
        return this.settingsViewer;
    }

    /**
     * Shows the settings dialog.
     */
    showSettingsDialog() {
        const settingsViewer = this.getOrCreateSettingsViewer();
        settingsViewer.showModal();
    }

    /**
     * Shows the world selection dialog.
     * Allows user to select a different world configuration to load.
     * @async
     */
    async showWorldSelectDialog() {
        if (this.refuseIfWorldLoadInProgress()) {
            return;
        }
        const currentWorldId = this.worldId || null;

        const selectedWorldId = await WorldSelectDialog.showWorldSelectDialog({
            appConfig: this.appConfig,
            currentWorldId: currentWorldId
        });

        if (selectedWorldId && selectedWorldId !== currentWorldId) {
            // User selected a different world - reload with new world
            // console.log(`User selected world: ${selectedWorldId}`);
            try {
                await this.clearWorldLoadNewWorldFileById(selectedWorldId);
            } catch (error) {
                // the menu click that got us here does not await us, so this would otherwise be an
                // unhandled rejection; loadAndInitializeWorld has already reported it (sidebar + log)
                console.error(`ERROR: Failed to switch to world "${selectedWorldId}":`, error);
            }
        }
    }

    /**
     * Shows the world information dialog with the current world's description.
     * @async
     */
    async showWorldInformation() {
        const description = this.worldConfig?.description || 'No description available.';
        const title = this.worldConfig?.label || 'World Information';

        await InformationDialog.showInformationDialog({
            title: title,
            htmlUnsafe: description
        });
    }


    /**
     * Callback invoked when settings are saved from SettingsViewer.
     * Updates any visual options that depend on settings.
     */
    onSettingsSaved() {

        // Push PDF reader settings (right margin gutter, fit-size zoom) to the viewer (app-scoped, so
        // this applies even while no world is loaded).
        if (this.pdfViewer) {
            this.pdfViewer.applyPdfReaderSettings();
        }

        // the rest acts on the world's searchers and map: nothing to do while there is no world
        if (!this.isWorldReady()) {
            return;
        }

        // advanced searcher stuff
        const advancedSearcher = this.getAdvancedSearcher();
        // if advanced tab is disabled, clear its contents
        if (advancedSearcher && !this.stateManager.getAdvancedSearchEnabled()) {
            advancedSearcher.performClearSearch(true);
        }
        // re-run all searches
        this.displayNeedsRefresh_AfterVisualOptionsChanged();
    }


    /**
     * Applies the current theme setting to the app-container element.
     * Removes any existing theme CSS classes and adds the new theme's classes.
     */
    applyTheme() {
        const themeId = this.stateManager.getTheme();
        const appContainer = document.getElementById(ID_APP_CONTAINER);

        // Remove all existing theme classes
        for (const theme of THEMES) {
            if (theme.cssClasses) {
                const classes = theme.cssClasses.split(' ').filter(c => c);
                appContainer.classList.remove(...classes);
            }
        }

        // Add new theme classes
        const selectedTheme = THEMES.find(t => t.id === themeId);
        if (selectedTheme && selectedTheme.cssClasses) {
            const classes = selectedTheme.cssClasses.split(' ').filter(c => c);
            appContainer.classList.add(...classes);
        }


        // now marker style stuff
        this.imposeMapMarkerStyles();
    }

    /**
     * Applies the yellow marker style from saved settings to the map manager.
     * Reads the marker style ID from state and resets the style cache.
     */
    imposeMapMarkerStyles() {
        // (applyTheme also runs from the app-scoped settings dialog: there are no map styles to set while
        // no world's map exists -- e.g. after a world config failed to load. Not isWorldReady(): the
        // world load itself calls this before the world counts as ready.)
        if (!this.mapManager || !this.mapManager.getMap()) {
            return;
        }
        const yellowMarkerStyleId = this.stateManager.getYellowMarkerStyleId();
        this.mapManager.setMarkerStyleId('yellow', yellowMarkerStyleId);
        this.mapManager.resetStyleCache();
    }



    /**
     * Retrieves a search searcher by its name.
     * @param {string} searcherName - Searcher identifier (see SEARCH_TYPES constant)
     * @returns {Searcher|undefined} The requested searcher instance or undefined if not found
     */
    getSearcherByName(searcherName) {
        if (!searcherName) {
            return null;
        }
        // own keys only: a name can come from stored data (a saved search's searchType), and a bare
        // lookup of "constructor" would hand back a function instead of "no such searcher"
        return getOwnValue(this.searchersByName, searcherName);
    }

    /**
     * Iterates over all searchers and calls the provided function for each.
     * @param {function(Searcher, string): void} fn - Callback receiving (searcher, searcherName)
     */
    forEachSearcher(fn) {
        for (const searcherName in this.searchersByName) {
            const searcher = this.getSearcherByName(searcherName);
            fn(searcher, searcherName);
        }
    }




    /**
     * Sets the map view to the specified location and zoom level.
     * @param {number} lon - Longitude coordinate
     * @param {number} lat - Latitude coordinate
     * @param {number} zoom - Zoom level
     */
    setView(lon, lat, zoom) {
        if (!this.isInitialized()) {
            return;
        }
        this.mapManager.setView(lon, lat, zoom);
    }

    /**
     * Gets the current map view state.
     * @returns {Object|null} View object with {lon, lat, zoom} properties, or null if not initialized
     */
    getView() {
        if (!this.isInitialized()) {
            return null;
        }
        return this.mapManager.getView();
    }



    /**
     * Gets the yellow pages category dictionary from the auxiliary data manager.
     * @returns {Object|null} Category dictionary object or null if not loaded
     */
    getPcatgdict() {
        return this.auxDataManager.getPcatgdict();
    }


    /**
     * Gets the resolved specialYellow data dictionary.
     * Each entry has the shape { label, items } where items is an array of
     * POI items (resolved against the POI database) plus any pass-through
     * non-POI entries.
     * @returns {Object|null} Resolved specialYellow data or null if not loaded
     */
    getSpecialYellowData() {
        return this.specialYellowData;
    }









    /**
     * Returns the StateManager instance for localStorage persistence.
     * @returns {StateManager} The state manager
     */
    getStateManager() {
        return this.stateManager;
    }

    /**
     * Returns the LogHelper instance for session logging.
     * @returns {LogHelper} The log helper
     */
    getLogHelper() {
        return this.logHelper;
    }

    /**
     * Returns the ImageHelper instance for icon/image path resolution.
     * @returns {ImageHelper} The image helper
     */
    getImageHelper() {
        return this.imageHelper;
    }

    /**
     * Returns the ImportExportHelper instance (import/export parsing and validation).
     * @returns {ImportExportHelper} The import/export helper
     */
    getImportExportHelper() {
        return this.importExportHelper;
    }

    /**
     * Returns the PlayLogger instance for the Log Play tab.
     * @returns {PlayLogger} The play logger
     */
    getPlayLogger() {
        return this.playLogger;
    }

    /**
     * Adds a log message with default (info) severity.
     * @param {string} message - The log message text
     */
    addMessage(message) {
        this.logHelper.addMessage(message);
    }

    /**
     * Adds an error-level log message.
     * @param {string} message - The error message text
     */
    addError(message) {
        this.logHelper.addError(message);
        const htmlSidebarErrorMessage = '<p>' + escapeHtml(message) + '</p>';
        this.addSidebarError(htmlSidebarErrorMessage)
        console.error(message);
    }

    /**
     * Adds a warning-level log message.
     * @param {string} message - The warning message text
     */
    addWarning(message) {
        this.logHelper.addWarning(message);
    }

    /**
     * Returns the PoiDataManager instance for POI data access and searching.
     * @returns {PoiDataManager} The POI data manager
     */
    getPoiDataManager() {
        return this.poiDataManager;
    }

    /**
     * Returns the showExtraPcatInfo setting value from state.
     * When true, extra category information is displayed in search results.
     * @returns {boolean} Whether extra pcat info should be shown
     */
    getShowExtraPcatInfo() {
        return this.stateManager.getShowExtraPcatInfo();
    }

    /**
     * Returns the AuxDataManager instance for auxiliary data access
     * (blocks, categories, locations, ad metadata).
     * @returns {AuxDataManager} The auxiliary data manager
     */
    getAuxDataManager() {
        return this.auxDataManager;
    }

    /**
     * Returns the AdvancedSearcher instance for full-text substring searches.
     * @returns {AdvancedSearcher} The advanced searcher
     */
    getAdvancedSearcher() {
        return this.advancedSearcher;
    }
    
    /**
     * Returns the SavedSearcher instance for managing favorited searches.
     * @returns {SavedSearcher} The saved search searcher
     */
    getSavedSearchSearcher() {
	    return this.savedSearcher;
    }

    /**
     * Returns the HistorySearcher instance for search history tracking.
     * @returns {HistorySearcher} The history searcher
     */
    getHistorySearcher() {
        return this.historySearcher;
    }

    /**
     * Returns the OpenLayersMapManager instance for map layer and view operations.
     * @returns {OpenLayersMapManager} The map manager
     */
    getMapManager() {
        return this.mapManager;
    }


    /**
     * @returns raster config setting or default value
     */
    getRasterConfigValue(key, defaultVal) {
        const rasterConfig = this.worldConfig.rasterConfig;
        if (rasterConfig) {
            if (rasterConfig[key]) {
                return rasterConfig[key];
            }
        }
        return defaultVal;
    }


    // ==========================================
    // SIDEBAR ERROR BANNER
    // ==========================================

    /**
     * Shows an error message in the sidebar error banner.
     * @param {string} message - The error message to display
     */
    setSidebarError(message) {
        const container = document.getElementById(DOM_ELEMENTS.ID_SIDEBAR_ERROR);
        const messageEl = document.getElementById(DOM_ELEMENTS.ID_SIDEBAR_ERROR_MESSAGE);
        if (container && messageEl) {
            messageEl.textContent = message;
            container.style.display = 'flex';
        }
    }

    /**
     * Appends HTML content to the sidebar error banner without replacing existing content.
     * Shows the banner if it is currently hidden.
     * @param {string} html - The HTML string to append
     */
    addSidebarError(html) {
        const container = document.getElementById(DOM_ELEMENTS.ID_SIDEBAR_ERROR);
        const messageEl = document.getElementById(DOM_ELEMENTS.ID_SIDEBAR_ERROR_MESSAGE);
        if (container && messageEl) {
            messageEl.innerHTML += html;
            container.style.display = 'flex';
        } else {
            alert(`Failed to find contianer to add sidebar error: ${html}`);
        }
    }

    /**
     * Clears and hides the sidebar error banner.
     */
    clearSidebarError() {
        const container = document.getElementById(DOM_ELEMENTS.ID_SIDEBAR_ERROR);
        if (container) {
            container.style.display = 'none';
        }
        const messageEl = document.getElementById(DOM_ELEMENTS.ID_SIDEBAR_ERROR_MESSAGE);
        if (messageEl) {
            messageEl.textContent = '';
        }
    }

    /**
     * Binds the dismiss button click handler for the sidebar error banner.
     * Called during UI initialization.
     */
    /**
     * Installs scroll event listeners on body and documentElement that reset
     * scrollTop to 0. This prevents the browser's scroll-on-focus behavior
     * from shifting the viewport when scrollIntoView() or .focus() is called
     * on elements inside the single-viewport layout.
     */
    installBodyScrollGuard() {
        const resetScroll = (el) => {
            if (el.scrollTop !== 0) {
                el.scrollTop = 0;
            }
            if (el.scrollLeft !== 0) {
                el.scrollLeft = 0;
            }
        };
        document.body.addEventListener('scroll', () => resetScroll(document.body));
        document.documentElement.addEventListener('scroll', () => resetScroll(document.documentElement));
    }


    initSidebarError() {
        // the close button is static DOM: bind once per page (see constructor), clear on every world load
        if (!this.sidebarErrorListenerInstalled) {
            this.sidebarErrorListenerInstalled = true;
            const closeBtn = document.getElementById(DOM_ELEMENTS.ID_SIDEBAR_ERROR_CLOSE);
            if (closeBtn) {
                closeBtn.addEventListener('click', () => this.clearSidebarError());
            }
        }
        // clear it initially
        this.clearSidebarError();
    }



    // ==========================================
    // LOADING DATA
    // ==========================================

    /**
     * Loads and registers all tile layers from configuration.
     * Supports both direct URL patterns and tilemapresource.xml-based configuration.
     * Activates the first layer and initializes the layer switcher control.
     * @async
     * @private
     */
    async loadTileLayers() {
        if (!this.worldConfig.tileLayers || this.worldConfig.tileLayers.length === 0) {
            throw new Error('ERROR: No tile layers configured for world.');
        }

        let firstBounds = null;

        // Use bounds from mapConfig if provided
        if (this.worldConfig.mapConfig && this.worldConfig.mapConfig.bounds) {
            firstBounds = this.worldConfig.mapConfig.bounds;
        }

        // Register all tile layers
        for (const layerConfig of this.worldConfig.tileLayers) {
            try {
                // Check if we need to load from tilemapresource.xml
                if (layerConfig.tilemapresourceXmlUrl) {
                    if (DEBUG_CBM) {
                        console.debug(`DEBUG: Loading tile configuration from tilemapresource.xml: ${layerConfig.tilemapresourceXmlUrl}`);
                    }
                    let url = fixRelativeChildPathWithParentFilePath(this.worldConfig.configPath, layerConfig.tilemapresourceXmlUrl);
                    url = appendCacheBuster(url, this.worldCacheBusterVersion);
                    // get xmlConfig
                    const xmlConfig = await this.configLoader.loadAndParseTilemapResourceXml(url);
                    // Append cache buster to tile URL template
                    const tileUrl = appendCacheBuster(xmlConfig.url, this.worldCacheBusterVersion);
                    // Merge layer config
                    const mergedLayerConfig = this.buildMergedLayerConfig(layerConfig, tileUrl, xmlConfig.tileFormat);
                    // Save bounds from first layer for fitting
                    if (!firstBounds) {
                        if (xmlConfig.bounds) {
                            firstBounds = xmlConfig.bounds;
                        }
                    }
                    // register tile layer
                    this.mapManager.registerTileLayer(mergedLayerConfig.url, mergedLayerConfig);
                } else if (layerConfig.tilemapDataUrl) {
                    // this is used for new .pmtile and .mbtile map data layers
                    // the tilemapDataUrl points to a file url with extension .pmtiles or .mbtiles
                    // we now need to register the tile with the mapManager and it should figure out how to load the map data
                    let url = fixRelativeChildPathWithParentFilePath(this.worldConfig.configPath, layerConfig.tilemapDataUrl);
                    // now add cache buster to url
                    url = appendCacheBuster(url, this.worldCacheBusterVersion);
                    // before we add cache buster file extension will tell us what format the tiles are in
                    let dataUrlFormat = layerConfig.tilemapDataUrl.split('.').pop();
                    // Merge layer config
                    const mergedLayerConfig = this.buildMergedLayerConfig(layerConfig, url, dataUrlFormat)
                    // register tile layer
                    this.mapManager.registerTileLayer(mergedLayerConfig.url, mergedLayerConfig);
                } else if (layerConfig.url) {
                    // Direct URL pattern provided
                    let url = fixRelativeChildPathWithParentFilePath(this.worldConfig.configPath, layerConfig.url);
                    url = appendCacheBuster(url, this.worldCacheBusterVersion);
                    this.mapManager.registerTileLayer(url, layerConfig);
                } else {
                    const errorMessage = `ERROR: Tile layer ${layerConfig.name} has neither url nor tilemapresourceXmlUrl`;
                    this.addError(errorMessage);
                }
            } catch (error) {
                const errorMessage = `ERROR: Failed to register tile layer ${layerConfig.name}: ${error}`;
                this.addError(errorMessage);
            }
        }

        // Preload PMTiles library if any registered layer uses pmtiles format
        const needsPMTiles = this.mapManager.tileSet.layers.some(
            layerInfo => layerInfo.options.tileFormat === 'pmtiles'
        );
        if (needsPMTiles) {
            await this.mapManager.loadPMTilesLibrary();
        }

        // Activate the first tile layer (will be overridden by saved state if available)
        if (this.mapManager.tileSet.layers.length > 0) {
            this.mapManager.activateTileSet(0);
            this.mapManager.setFirstBounds(firstBounds);
        }

        // Initialize layer switcher if there are multiple layers
        this.mapManager.initializeTileSetSwitcher();
    }



    buildMergedLayerConfig(layerConfig, url, tileFormat) {
        const retv = {
            ...layerConfig,
            url: url,
            // ?? not ||: a minZoom of 0 is a real value; an absent maxZoom stays undefined (never null)
            // so _createTileLayer's own default applies instead of a one-zoom grid
            minZoom: layerConfig.minZoom ?? 1,
            maxZoom: layerConfig.maxZoom ?? undefined,
            tileFormat: tileFormat,
        };
        return retv;
    }

    

    /**
     * Loads icon mappings and precomputed image lists into the ImageHelper.
     * @async
     * @private
     */
    async initializeImageHelper() {
        await this.imageHelper.loadIconMapFromPath(IMAGE_MAP_PATH);
        await this.imageHelper.loadImageLists(IMAGE_MAP_PATH, USER_ICON_PICKER_IMAGELIST);
        //await this.imageHelper.loadImageList(IMAGE_MAP_PATH, 'user');
        //await this.imageHelper.loadImageList(IMAGE_MAP_PATH, 'icons');
        await this.imageHelper.loadImageList(IMAGE_MAP_PATH, 'markers');
    }


    /**
     * Initializes the PoiDataManager and loads all configured data sources.
     * Data sources (places, people) are loaded into memory and indexed for searching.
     * @async
     * @private
     */
    async loadPoiDataSources(worldConfig) {
        // Check if data sources are configured
        if (!this.worldConfig.dataSources) {
            throw new Error('ERROR: No data sources configured for worldCOnfig');
        }

        try {
            this.poiDataManager = new PoiDataManager(this, this.worldConfig.configPath, this.worldConfig.dataSources);

            // Load all data sources. The POI data keeps its file order: there is deliberately no
            // shuffle (user decision, Oct 2026 -- the old dataSourcesNeedShuffling call passed no data
            // and so never shuffled anything; it was removed rather than switched on, and the
            // world configs no longer carry that key).
            await this.poiDataManager.loadAll();
        } catch (error) {
            const errorMessage = `ERROR: Exception while trying to load poi data sources: ${error}`;
            this.addError(errorMessage);
            throw new Error(error);
        }
    }



    /**
     * Initializes the AuxDataManager and loads auxiliary data sources
     * (blocks, categories, locations, ad metadata).
     * @async
     * @private
     */
    async loadAuxDataSources() {
        try {
            this.auxDataManager = new AuxDataManager(this, this.worldConfig.configPath, this.worldConfig.auxData);
            await this.auxDataManager.loadAll();
        } catch (error) {
            const errorMessage = `ERROR: Failed to load aux data sources: ${error}`;
            this.addError(errorMessage);
            throw new Error(error);
        }
        // tell yellow searcher about the pcatg data
        const pcatgdict = this.auxDataManager.getPcatgdict();
        this.yellowSearcher.populateCategories(pcatgdict);

        // resolve specialYellow stubs against the POI database, then append
        // the SPECIAL group to the bottom of the Yellow dropdown
        this.createSpecialYellowData();
        this.yellowSearcher.addSpecialYellowPageSections(this.specialYellowData);
    }


    /**
     * Resolves the raw specialYellow data loaded by AuxDataManager into
     * render-ready item arrays. POI stubs are looked up by uid; entries with
     * a non-POISTUB ptype (e.g. PTYPES.CUSTOM) are passed through unchanged.
     * Stubs that cannot be located in the POI database are silently dropped,
     * matching the bookmark deserialization behavior.
     * @private
     */
    createSpecialYellowData() {
        const rawData = this.auxDataManager.getRawSpecialYellowData();
        if (!rawData) {
            this.specialYellowData = null;
            return;
        }
        const resolved = {};
        for (const [listId, entry] of Object.entries(rawData)) {
            const items = [];
            const rawItems = entry.items || [];
            for (const rawItem of rawItems) {
                if (!rawItem || typeof rawItem !== 'object') {
                    continue;
                }
                // Treat anything without an explicit non-stub ptype as a POI stub.
                // Curated entries carry no meaningful result count, so default
                // searchTotalCount to -1, which tells the lister to suppress the
                // "N results" line (an explicit count in the data still wins).
                const stub = (rawItem.ptype && rawItem.ptype !== PTYPES.POISTUB)
                    ? { searchTotalCount: -1, ...rawItem }
                    : { ...rawItem, ptype: PTYPES.POISTUB };
                const resolvedItem = this.locateAndModifyPoiFromLoadedUserStub(stub, CUSTOM_ITEM_FIELDS_ALL_PLUSTEMPS, true);
                // Drop unresolved POI stubs (they would have ptype POISTUB still)
                if (resolvedItem && resolvedItem.ptype !== PTYPES.POISTUB) {
                    items.push(resolvedItem);
                } else {
                    // push raw item if not found
                    const wrappedItem = rawItem;
                    items.push(wrappedItem);
                }
            }
            resolved[listId] = { label: entry.label, items };
        }
        this.specialYellowData = resolved;
    }







    // ==========================================
    // MISCELANEOUS UI STUFF
    // ==========================================




    /**
     * Ensures the main panel (map) is visible by collapsing full-width sidebar if active.
     */
    ensureMainPanelVisible() {
        this.uiManager.ensureMainPanelVisible();
    }


    /**
     * Navigates to and selects a result item by its uid across all searchers.
     * Prioritizes the currently visible tab, then checks other searchers.
     * @param {string} uid - The unique identifier of the item to select
     * @returns {boolean} True if the item was found and selected, false otherwise
     */
    someoneElseInstructsGoToAndSelectResultItemByUid(uid, flagDramatically) {
        // ATTN: THIS IS ONLY CALLED WHEN CLICKING THE BLUE "NAVIGATE TO" button in the pdf, NOT when we just read a pdf entry!
        //console.log('ATTN: User someoneElseInstructsGoToAndSelectResultItemByUid on uid: ${uid}.')

        // Ensure sidebar is visible so the user can see the selected result
        this.uiManager.ensureSidebarExpanded();

        // 1. Build a list of all the searchers, with the currently visible tab's searcher first
        const activeSearcherName = this.calcActiveSearcherName();

        // Create ordered list of searcher names with active one first
        const searcherNames = Object.keys(this.searchersByName);
        const orderedSearcherNames = [
            activeSearcherName,
            ...searcherNames.filter(name => name !== activeSearcherName)
        ];

        // 2. Iterate through searchers, stopping at the first one that finds the item
        for (const searcherName of orderedSearcherNames) {
            const searcher = this.getSearcherByName(searcherName);
            if (!searcher) {
                continue;
            }
            //
            const found = searcher.hasResultItemOnListerByUid(uid);
            if (found) {
                // it was found on a searched; if its not the one already current we switch to it
                if (searcherName != activeSearcherName) {
                    // we have to switch to that tab since we were not on it
                    this.switchToTabBySearcherName(searcherName);
                }
            // ATTN: we are just restoring so no smooth scrolling, but yes it should be in view; if its from map marker click we might animate to it rather than jump to it
            const flagScrollIntoView = true;
            const fromMapMarkerClick = true;
            const flagFlashMarker = flagDramatically;
            const flagUserInitiated = true;
            searcher.setSelectedResultOnOurListerByUid(uid, flagScrollIntoView, fromMapMarkerClick, flagFlashMarker, flagUserInitiated);
            return true;
            }
        }

        // not found on any result tab -- BUT we may really want to jump to this loacation if player clicked on it in pdf
        // SO we have a workaround, we will add it to HISTORY and then go to it in history
        const item = this.poiDataManager.findItemByUid(uid);
        if (item) {
            console.log(`Adding jump target uid to history.`);
            const historySearcher = this.getHistorySearcher();
            // unselect whatever is already selected on this searcher lister?
            historySearcher.clearCurrentSearcherSelection();
            // now go to history tab if not actuve
            if (activeSearcherName!=historySearcher.getName()) {
                this.switchToTabBySearcherName(historySearcher.getName());
            }
            // add item
            historySearcher.addHistoryItemDirect(item);
            //await new Promise(requestAnimationFrame);
            //await new Promise(requestAnimationFrame);
            // go to item
            const flagScrollIntoView = true;
            const fromMapMarkerClick = true;
            const flagFlashMarker = flagDramatically;
            const flagUserInitiated = true;
            historySearcher.setSelectedResultOnOurListerByUid(uid, flagScrollIntoView, fromMapMarkerClick, flagFlashMarker, flagUserInitiated);
            return true;
        }

        // 3. Return false if no searcher found the item
        return false;
    }





    genericPdfLeadBookmarkClicked(label, pageNumber) {
        // use visits a bookmark in the pdf reader, we now want to add a PDFLEAD item to history
        //console.log(`ATTN: In genericPdfLeadBookmarkClicked with "${label}".`)
        const historySearcher = this.getHistorySearcher();
        if (!historySearcher) {
            // no world loaded (the pdf viewer outlives world switches)
            return false;
        }
        // unselect whatever is already selected on this searcher lister?
        historySearcher.clearCurrentSearcherSelection();
        // create item and add it
        historySearcher.addPdfLeadItemManually(label, pageNumber);
        return true;
    }







    // ==========================================
    // MAIN PANEL TAB MANAGEMENT
    // ==========================================


    /**
     * Shows or hides the main panel tab navigation.
     * When hidden, only the map is visible (default behavior).
     * When shown, users can switch between the map and other tabs.
     * @param {boolean} visible - Whether the tab navigation should be visible
     */
    setMainPanelTabsVisible(visible) {
        const tabsEl = document.getElementById(ID_MAIN_PANEL_TABS);
        if (tabsEl) {
            tabsEl.style.display = visible ? '' : 'none';
            // Set up listeners if not already done
            if (visible && !this._readingTabListenerAdded) {
                this._readingTabListenerAdded = true;
                const readingTab = document.getElementById(ID_MAIN_READING_TAB);
                if (readingTab) {
                    readingTab.addEventListener('shown.bs.tab', () => {
                        this.ensurePdfViewerInitialized();
                        this.focusPdfReaderAfterTabShown(readingTab);
                    });
                }
                // Set up close button listener
                const closeBtn = document.getElementById(ID_MAIN_READING_TAB_CLOSE);
                if (closeBtn) {
                    closeBtn.addEventListener('click', (e) => {
                        e.stopPropagation();
                        this.closeReadingTab();
                    });
                }
            }
        }
    }

    /**
     * Returns whether the main panel tabs are currently visible.
     * @returns {boolean} True if tabs are visible, false if hidden
     */
    getMainPanelTabsVisible() {
        const tabsEl = document.getElementById(ID_MAIN_PANEL_TABS);
        return tabsEl && tabsEl.style.display !== 'none';
    }

    /**
     * Shows or hides a single main-panel tab button by its tab id (the tab pane is left untouched).
     * @param {string} tabId - The ID of the tab button element
     * @param {boolean} hidden - Whether the tab button should be hidden
     */
    setMainPanelTabHidden(tabId, hidden) {
        const li = document.getElementById(tabId)?.closest('li');
        if (li) { li.style.display = hidden ? 'none' : ''; }
    }

    /**
     * Returns whether the given main-panel tab button exists and is currently visible.
     * @param {string} tabId - The ID of the tab button element
     * @returns {boolean}
     */
    isMainPanelTabVisible(tabId) {
        const li = document.getElementById(tabId)?.closest('li');
        return !!(li && li.style.display !== 'none');
    }

    /**
     * Hides the whole main-panel tab bar once every tab except the Map tab is closed/hidden.
     * The Map tab is always present, so a bar showing only the map is redundant and gets hidden,
     * returning to the default map-only view.
     */
    hideMainPanelTabsIfOnlyMapRemains() {
        const tabsEl = document.getElementById(ID_MAIN_PANEL_TABS);
        if (!tabsEl) { return; }
        let visibleNonMapTabs = 0;
        tabsEl.querySelectorAll('.nav-item').forEach((li) => {
            const btn = li.querySelector('.nav-link');
            if (!btn || btn.id === ID_MAIN_MAP_TAB) { return; }
            if (li.style.display !== 'none') { visibleNonMapTabs++; }
        });
        if (visibleNonMapTabs === 0) {
            this.switchToMainPanelMapTab();
            this.setMainPanelTabsVisible(false);
        }
    }

    /**
     * Switches to the map tab in the main panel.
     * This shows the map container.
     */
    switchToMainPanelMapTab() {
        this.switchToMainPanelTabById(ID_MAIN_MAP_TAB);
    }

    /**
     * Switches to a specific tab in the main panel by its tab button ID.
     * @param {string} tabId - The ID of the tab button element to activate
     */
    switchToMainPanelTabById(tabId) {
        const tabEl = document.getElementById(tabId);
        if (tabEl) {
            const bsTab = new bootstrap.Tab(tabEl);
            bsTab.show();
        }
    }

    /**
     * Switches to a tab and returns a promise that resolves after the transition completes.
     * @param {string} tabId - The ID of the tab button element to activate
     * @returns {Promise<void>}
     */
    switchToMainPanelTabByIdAsync(tabId) {
        return new Promise((resolve) => {
            const tabEl = document.getElementById(tabId);
            if (!tabEl) { resolve(); return; }
            tabEl.addEventListener('shown.bs.tab', () => resolve(), { once: true });
            const bsTab = new bootstrap.Tab(tabEl);
            bsTab.show();
        });
    }

    /**
     * Returns the ID of the currently active main panel tab.
     * @returns {string|null} The ID of the active tab button, or null if none found
     */
    getActiveMainPanelTabId() {
        const tabsEl = document.getElementById(ID_MAIN_PANEL_TABS);
        if (!tabsEl) return null;
        const activeTab = tabsEl.querySelector('.nav-link.active');
        return activeTab ? activeTab.id : null;
    }

    /**
     * Checks if the map tab is currently the active tab in the main panel.
     * @returns {boolean} True if map tab is active
     */
    isMainPanelMapTabActive() {
        return this.getActiveMainPanelTabId() === ID_MAIN_MAP_TAB;
    }

    /**
     * Adds a new tab to the main panel.
     * @param {string} tabId - Unique ID for the tab button
     * @param {string} paneId - Unique ID for the tab pane content container
     * @param {string} label - Display label for the tab
     * @param {string} [contentHtml=''] - Optional initial HTML content for the pane
     * @returns {HTMLElement} The created tab pane element
     */
    addMainPanelTab(tabId, paneId, label, contentHtml = '') {
        const tabsEl = document.getElementById(ID_MAIN_PANEL_TABS);
        const contentEl = document.getElementById(ID_MAIN_PANEL_CONTENT);

        if (!tabsEl || !contentEl) {
            console.error('Main panel tabs or content container not found');
            return null;
        }

        // Create tab button
        const li = document.createElement('li');
        li.className = 'nav-item';
        li.setAttribute('role', 'presentation');

        const button = document.createElement('button');
        button.className = 'nav-link';
        button.id = tabId;
        button.setAttribute('data-bs-toggle', 'tab');
        button.setAttribute('data-bs-target', `#${paneId}`);
        button.setAttribute('type', 'button');
        button.setAttribute('role', 'tab');
        button.textContent = label;

        li.appendChild(button);
        tabsEl.appendChild(li);

        // Create tab pane
        const pane = document.createElement('div');
        pane.className = 'tab-pane fade';
        pane.id = paneId;
        pane.setAttribute('role', 'tabpanel');
        pane.innerHTML = contentHtml;

        contentEl.appendChild(pane);

        return pane;
    }

    /**
     * Removes a tab from the main panel by its tab button ID.
     * Cannot remove the map tab.
     * @param {string} tabId - The ID of the tab button to remove
     * @returns {boolean} True if tab was removed, false if not found or is map tab
     */
    removeMainPanelTab(tabId) {
        if (tabId === ID_MAIN_MAP_TAB) {
            console.warn('Cannot remove the map tab');
            return false;
        }

        const tabEl = document.getElementById(tabId);
        if (!tabEl) return false;

        // Get pane ID from the tab's data-bs-target
        const paneId = tabEl.getAttribute('data-bs-target')?.replace('#', '');
        const paneEl = paneId ? document.getElementById(paneId) : null;

        // If this tab is active, switch to map first
        if (tabEl.classList.contains('active')) {
            this.switchToMainPanelMapTab();
        }

        // Remove tab button (and its parent li)
        tabEl.closest('li')?.remove();

        // Remove tab pane
        paneEl?.remove();

        return true;
    }

    /**
     * Gets the tab pane element for a given tab button ID.
     * @param {string} tabId - The ID of the tab button
     * @returns {HTMLElement|null} The tab pane element, or null if not found
     */
    getMainPanelTabPane(tabId) {
        const tabEl = document.getElementById(tabId);
        if (!tabEl) return null;

        const paneId = tabEl.getAttribute('data-bs-target')?.replace('#', '');
        return paneId ? document.getElementById(paneId) : null;
    }

    /**
     * Switches to the reading/Case Book tab in the main panel.
     */
    switchToMainPanelReadingTab() {
        // The Case Book tab may have been hidden by a prior close; make sure it's shown again.
        this.setMainPanelTabHidden(ID_MAIN_READING_TAB, false);
        this.switchToMainPanelTabById(ID_MAIN_READING_TAB);
        // Ensure PDF viewer is initialized so URL bar is visible
        this.ensurePdfViewerInitialized();
    }

    /**
     * Checks if the reading/Case Book tab is currently active.
     * @returns {boolean} True if reading tab is active
     */
    isMainPanelReadingTabActive() {
        return this.getActiveMainPanelTabId() === ID_MAIN_READING_TAB;
    }

    /**
     * Moves the keyboard focus into the PDF reader once its tab has been shown, if the focus is on
     * nothing or on the Case Book tab button after a CLICK: a clicked button keeps the focus, so the
     * reader's keys went to the tab button. Left alone: a keyboard user who moved onto the tab with
     * the arrow keys (the button is :focus-visible then), and a focus anywhere else, such as a
     * sidebar field the user is typing in when Ctrl+Q brings the reader up.
     * @param {HTMLElement} readingTab - The Case Book tab button
     */
    focusPdfReaderAfterTabShown(readingTab) {
        const active = document.activeElement;
        const nothingFocused = (!active || active === document.body || active === document.documentElement);
        const tabClicked = (active === readingTab && !readingTab.matches(':focus-visible'));
        if ((nothingFocused || tabClicked) && this.pdfViewer) {
            this.pdfViewer.focusViewer();
        }
    }

    /**
     * Closes the reading/Case Book tab. Hides just the Case Book tab button; the whole tab bar is
     * hidden only once nothing but the Map tab remains (see hideMainPanelTabsIfOnlyMapRemains).
     */
    closeReadingTab() {
        if (this.isMainPanelReadingTabActive()) {
            this.switchToMainPanelMapTab();
        }
        this.setMainPanelTabHidden(ID_MAIN_READING_TAB, true);
        this.hideMainPanelTabsIfOnlyMapRemains();
    }

    /**
     * Gets the reading pane content element.
     * @returns {HTMLElement|null} The reading-pane element
     */
    getReadingPaneElement() {
        return document.getElementById(ID_READING_PANE);
    }


    /**
     * Ensures the "Background Music" main-panel tab + pane exist and the BackgroundMusicPlayer is
     * created, returning the player. The tab carries a speaker icon and a close affordance (like the
     * Case Book tab) and stays hidden until revealed by a caller.
     * @returns {BackgroundMusicPlayer|null}
     * @private
     */
    _ensureBackgroundMusicPlayer() {
        // the main-panel tab bar must be visible to host the tab
        this.setMainPanelTabsVisible(true);

        let pane = document.getElementById(ID_MAIN_BGMUSIC_PANE);
        if (!pane) {
            pane = this.addMainPanelTab(ID_MAIN_BGMUSIC_TAB, ID_MAIN_BGMUSIC_PANE, 'Background Music');
            if (!pane) { return null; }

            // add a speaker icon at the end of the title, then a close affordance (mirrors the Case Book tab)
            const tabBtn = document.getElementById(ID_MAIN_BGMUSIC_TAB);
            if (tabBtn) {
                const speakerIcon = document.createElement('i');
                speakerIcon.className = 'bi bi-volume-up bgmusic-tab-icon';
                tabBtn.appendChild(speakerIcon);

                const closeSpan = document.createElement('span');
                closeSpan.className = 'main-panel-tab-close';
                closeSpan.id = ID_MAIN_BGMUSIC_TAB_CLOSE;
                closeSpan.setAttribute('aria-label', 'Close Background Music tab');
                closeSpan.innerHTML = '<i class="bi bi-x"></i>';
                closeSpan.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.closeBackgroundMusicTab();
                });
                tabBtn.appendChild(closeSpan);
            }
        }

        if (!this.bgMusicPlayer) {
            this.bgMusicPlayer = new BackgroundMusicPlayer(pane, {
                stateManager: this.stateManager
            });
        }
        return this.bgMusicPlayer;
    }


    /**
     * Returns the current world's m3u music playlists: worldConfig.defaultMusicPlaylists, with every url
     * resolved to a fully absolute url (scheme+host), falling back to MAPAPP_DEFAULTS.DEFAULT_MUSIC_PLAYLISTS
     * when the world does not define any.
     * @returns {Array<{label: string, url: string}>}
     * @private
     */
    _getWorldMusicPlaylists() {
        const raw = (this.worldConfig && Array.isArray(this.worldConfig.defaultMusicPlaylists))
            ? this.worldConfig.defaultMusicPlaylists
            : MAPAPP_DEFAULTS.DEFAULT_MUSIC_PLAYLISTS;
        const configPath = this.worldConfig ? this.worldConfig.configPath : null;
        return (raw || [])
            .filter(p => p && p.url)
            // resolve to a fully absolute url so the value stored/serialized in the global playlist list is
            // unambiguous, regardless of which world is loaded (or the page url) when it is later used
            .map(p => ({ label: p.label, url: this._resolveAbsoluteMusicUrl(p.url, configPath) }));
    }


    /**
     * Resolves a (possibly relative) music playlist url to a fully absolute url. Relative paths (./, ../,
     * bare) and site-relative ("/media/...") urls resolve against the world config path / page origin;
     * already-absolute urls are returned unchanged. Falls back to the raw url if it cannot be parsed.
     * @param {string} rawUrl
     * @param {string|null} configPath - The world config url the playlist url may be relative to
     * @returns {string}
     * @private
     */
    _resolveAbsoluteMusicUrl(rawUrl, configPath) {
        try {
            const origin = window.location.origin;
            const base = configPath ? new URL(configPath, origin).href : origin;
            return new URL(rawUrl, base).href;
        } catch (e) {
            return rawUrl;
        }
    }


    /**
     * Merges the current world's m3u playlists into the user's persisted known list (deduped by url).
     * Called on every world load, so a world's playlists re-appear even if the user removed them. If the
     * Background Music tab is currently open, its playlist dropdown is refreshed.
     * @private
     */
    _mergeWorldMusicPlaylists() {
        const worldPlaylists = this._getWorldMusicPlaylists();
        for (const p of worldPlaylists) {
            this.stateManager.addMusicPlaylist(p.label, p.url);
        }
        if (this.bgMusicPlayer) { this.bgMusicPlayer.refreshPlaylists(); }
    }


    /**
     * Adds an m3u playlist (if new), selects it, and starts playing it in the Background Music tab.
     * Triggered by PDF links to .m3u files. We intentionally do NOT switch to this tab, so the user
     * keeps reading uninterrupted; the tab button is just revealed.
     * @param {string} url - The .m3u playlist url (may carry the ?backgroundLoop marker, which is stripped)
     */
    playPdfPlaylist(url) {
        const playlistUrl = PdfViewer.stripBackgroundLoop(url);
        if (!playlistUrl) { return; }

        const player = this._ensureBackgroundMusicPlayer();
        if (!player) { return; }

        // reveal the tab button (hidden until used), without switching to it
        this.setMainPanelTabHidden(ID_MAIN_BGMUSIC_TAB, false);

        player.playPlaylistUrl(playlistUrl);
    }


    /**
     * Adds a single track to the User Playlist (if new), selects the User Playlist, and plays it in the
     * Background Music tab. Triggered by PDF links to individual audio files. With {loop:true} (legacy
     * ?backgroundLoop links) the track is looped. The tab is revealed but not switched to.
     * @param {string} url - The media url (already stripped of the ?backgroundLoop marker by the caller)
     * @param {Object} [opts]
     * @param {boolean} [opts.loop=false]
     */
    playPdfTrack(url, opts = {}) {
        if (!url) { return; }

        const player = this._ensureBackgroundMusicPlayer();
        if (!player) { return; }

        // reveal the tab button (hidden until used), without switching to it
        this.setMainPanelTabHidden(ID_MAIN_BGMUSIC_TAB, false);

        player.playTrackUrl(url, opts);
    }


    /**
     * Opens (and switches to) the Background Music tab as a playlist manager, without auto-playing
     * anything. Wired to the sidebar "Background music" menu item.
     */
    openBackgroundMusicTab() {
        const player = this._ensureBackgroundMusicPlayer();
        if (!player) { return; }
        this.setMainPanelTabHidden(ID_MAIN_BGMUSIC_TAB, false);
        player.render();
        this.switchToMainPanelTabById(ID_MAIN_BGMUSIC_TAB);
    }


    /**
     * Toggles the Background Music tab: opens (and switches to) it if hidden, closes it if shown.
     * Wired to the sidebar "Toggle Background Music Tab" menu item.
     */
    toggleBackgroundMusicTab() {
        if (this.getMainPanelTabsVisible() && this.isMainPanelTabVisible(ID_MAIN_BGMUSIC_TAB)) {
            this.closeBackgroundMusicTab();
        } else {
            this.openBackgroundMusicTab();
        }
    }


    /**
     * Stops background music playback and removes the Background Music tab. The saved playlist and
     * check-states are left intact (reopen via the menu or a QR link).
     */
    closeBackgroundMusicTab() {
        if (this.bgMusicPlayer) { this.bgMusicPlayer.stop(); }
        this.removeMainPanelTab(ID_MAIN_BGMUSIC_TAB);
        // the pane is removed with the tab; drop the player so it is rebuilt fresh next time
        this.bgMusicPlayer = null;
        this.hideMainPanelTabsIfOnlyMapRemains();
    }


    // ==========================================
    // PDF VIEWER
    // ==========================================


    /**
     * Gets the PDF viewer instance, creating it if necessary.
     * The PDF viewer is lazily initialized on first use.
     * @returns {PdfViewer} The PDF viewer instance
     */
    getPdfViewer() {
        if (!this.pdfViewer) {
            const container = this.getReadingPaneElement();
            if (!container) {
                console.error('Reading pane element not found');
                return null;
            }
            this.pdfViewer = new PdfViewer(container, {
                stateManager: this.stateManager,
                onBookmarkClick: (label, flagGoToOnMap, pageNumber) => this.userClicksPdfBookmark(label, flagGoToOnMap, pageNumber),
                shouldLinkPdfBookmark: (titleLabel) => this.shouldLinkPdfBookmark(titleLabel),
                onCopyToPlayLog: (text, pageNum, bookmarkLabel, prefix) => {
                    if (text.length > PLAYLOG_CONSTANTS.MAX_COPY_TEXT_LEN) {
                        text = text.substring(0, PLAYLOG_CONSTANTS.MAX_COPY_TEXT_LEN) + "...";
                    }
                    const location = bookmarkLabel ? `${bookmarkLabel}, p.${pageNum}` : `p.${pageNum}`;
                    this.getPlayLogger().addLogText(`* PDF ${prefix}: ${location} - "${text}"`);
                },
                onLeadGoToAttempt: (leadInput, flagFound) => this.logPlayPdfLeadGoTo(leadInput, flagFound),
                onPlaylistMedia: (url) => this.playPdfPlaylist(url),
                onTrackMedia: (url, opts) => this.playPdfTrack(url, opts)
            });
        }
        return this.pdfViewer;
    }

    /**
     * Returns whether a PDF bookmark title should be rendered as a hyperlink.
     * @param {string} titleLabel - The bookmark title text
     * @returns {boolean} True if the title matches a POI lead
     */
    shouldLinkPdfBookmark(titleLabel) {
        //console.log(`Checking shouldLinkPdfBookmark(${titleLabel})`);
        // the PDF viewer outlives world switches, so it can render a page while a world is loading
        if (!this.poiDataManager) {
            return false;
        }
        const item = this.poiDataManager.findItemByLead(titleLabel);
        return (item != null);
    }

    /**
     * Called when a user clicks a bookmark or link in the PDF viewer.
     * @param {string|null} id - The bookmark/destination ID (null for explicit destination arrays)
     * @param {string|null} label - The bookmark title/label (null for annotation links)
     */
    userClicksPdfBookmark(label, flagGoToOnMap, pageNumber) {
        //console.log(`userClicksPdfBookmark label="${label}" with flagGoToOnMap=${flagGoToOnMap} and p=${pageNumber}.`);

        // play log it
        this.logPlayPdfBookmarkClick(label, pageNumber);

        // The PDF viewer outlives world switches, so this can fire while a world is loading or after a
        // world config failed to load: there is then no poi data, map or History tab to update.
        if (!this.isWorldReady()) {
            return;
        }

        let leadId = getDefaultLeadIdPatterner().normalizeLeadId(label);
        const item = this.poiDataManager ? this.poiDataManager.findItemByLead(leadId) : null;
        if (item) {
            const uid = item.uid;
            if (flagGoToOnMap) {
                this.ensureMainPanelVisible();
                this.ensureMapTabVisible();
                // ATTN: note that this item may not be in ANY lister result (!)
                this.someoneElseInstructsGoToAndSelectResultItemByUid(uid, true);
                // do we need to do this? or will above handle it?
                // this.onItemSelected(uid, item, null, true, true);
            } else {
                // ATTN: TODO: we should still add to history
                // this is ugly kludge repeated code from someoneElseInstructsGoToAndSelectResultItemByUid
                const historySearcher = this.getHistorySearcher();
                // unselect whatever is already selected on this searcher lister?
                //historySearcher.clearCurrentSearcherSelection();
                // add item
                historySearcher.addHistoryItemDirect(item);
            }
        } else {
            // no poi item found
            // IF its shcd like location compatible address, go there on reverse tab
            if (this.isReverseSearchString(label)) {
                if (flagGoToOnMap) {
                    this.ensureMainPanelVisible();
                    this.ensureMapTabVisible();
                    this.goToReverseSearchString(label);
                    return;
                }
                else {
                    // nothing to do
                }
            } else {
                // NEW 5/9/26 we have not found a lead this bookmark corresponds to, BUT we would like to add a HISTORY entry for this bookmark so its in history and can be returned to someoneElseInstructsGoToAndSelectResultItemByUid
                this.genericPdfLeadBookmarkClicked(label, pageNumber);
            }
        }
    }

    isReverseSearchString(text) {
        // return true if text is a reverse search location
        const { jregion, loclabel } = parseBlockQueryString(text);
        if (jregion && loclabel) {
            return true;
        }
        return false;
    }
    goToReverseSearchString(text) {
        // run a reverse search for text
        this.programmaticallyRunSearchOnQuery(SEARCH_TYPES.REVERSE, text, true);
    }


    /**
     * Loads a PDF file into the reading pane viewer.
     * Shows the main panel tabs and switches to the reading tab.
     * @param {string} url - The URL of the PDF file to load
     * @param {string|Array<string>} [expandSectionLabelId] - Bookmark section label(s) to expand (PdfViewer.loadPdf)
     * @param {boolean} [showTabs=true] - Whether to show the main panel tabs
     * @param {boolean} [switchToTab=true] - Whether to switch to the reading tab
     * @returns {Promise<void>}
     */
    async loadPdfInReadingPane(url, expandSectionLabelId, showTabs = true, switchToTab = true) {
        const viewer = this.getPdfViewer();
        if (!viewer) return;

        if (showTabs) {
            this.setMainPanelTabsVisible(true);
        }

        if (switchToTab) {
            this.switchToMainPanelReadingTab();
        }

        // logged only when it really loaded: loadPdf drops a request made while another load is
        // running, and shows (rather than throws) a failure
        if (await viewer.loadPdf(url, expandSectionLabelId)) {
            this.logPlayPdfLoaded(url);
        }
    }

    /**
     * Loads a PDF specified via the startup `?pdfUrl=` URL parameter.
     * Records the url in the PDF URL history (so it appears in the Load PDF dialog's recent
     * list and becomes the last-loaded url), then loads it into the reading pane, showing the
     * main panel tab bar and switching to the Case Book reading tab.
     * @param {string} url - The PDF URL to load
     * @returns {Promise<void>}
     */
    async loadStartupPdf(url) {
        // remember it in history (this also sets it as the last-loaded pdf url)
        this.stateManager.addPdfUrlToHistory(url);
        // show tabs, switch to the reading tab, and load the pdf
        await this.loadPdfInReadingPane(url, LEADS_SECTION_LABELS);
    }

    /**
     * Clears the PDF from the reading pane viewer.
     * @returns {Promise<void>}
     */
    async clearPdfViewer() {
        if (this.pdfViewer) {
            await this.pdfViewer.clear();
        }
    }

    /**
     * Checks if a PDF is currently loaded in the viewer.
     * @returns {boolean} True if a PDF is loaded
     */
    hasPdfLoaded() {
        return this.pdfViewer && this.pdfViewer.hasPdf();
    }

    /**
     * Returns the base name (no path, no extension) of the currently loaded
     * PDF file or URL, or null if no PDF is loaded.
     * @returns {string|null}
     */
    getBasePdfFilename() {
        if (!this.pdfViewer || !this.pdfViewer.hasPdf()) return null;
        const filename = this.pdfViewer.getCurrentFileName() || '';
        const dotIdx = filename.lastIndexOf('.');
        return dotIdx > 0 ? filename.substring(0, dotIdx) : filename;
    }

    /**
     * Gets all bookmark labels from the currently loaded PDF as a newline-separated string.
     * @returns {Promise<string>} Newline-separated bookmark titles, or empty string if no PDF loaded
     */
    async getPdfLeads() {
        const pdfViewer = this.getPdfViewer();
        if (!pdfViewer.hasPdf()) {
            this.ensurePdfViewerVisible();
            await this.loadLastPdfIfNothingLoaded(true);
        }
        if (!pdfViewer.hasPdf()) {
            return '';
        }
        return await pdfViewer.calcPdfLeads();
    }

    /**
     * Navigates to a named destination/bookmark in the currently loaded PDF.
     * @param {string} id - The bookmark/named destination ID
     * @returns {Promise<boolean>} True if the destination was found and navigated to
     */
    async goToPdfBookmark(id) {
        if (!this.pdfViewer || !this.pdfViewer.hasPdf()) {
            return false;
        }
        return await this.pdfViewer.goToBookmark(id);
    }

    /**
     * Ensures the PDF viewer is initialized (creates UI without loading a PDF).
     * Call this when showing the reading tab to display the URL bar.
     */
    ensurePdfViewerInitialized() {
        this.getPdfViewer();
    }


    // ==========================================
    // SAVE & LOAD STATE
    // ==========================================


    /**
     * Builds a map config object with saved state merged in.
     * This allows the map to initialize with the correct view immediately,
     * avoiding a visual jump from default to saved state.
     * @param {Object} baseConfig - The base map config from worldConfig
     * @returns {Object} Merged config with saved view state
     * @private
     */
    buildMapConfigWithSavedState(baseConfig) {
        const savedView = this.stateManager.getMapView();
        if (!savedView) {
            // No saved state, use base config as-is
            return baseConfig;
        }

        // Merge saved view into config
        return {
            ...baseConfig,
            center: savedView.center,
            defaultViewZoom: savedView.zoom
        };
    }


    /**
     * Restores saved application state from localStorage.
     * Restores the active tile layer. Map view (center/zoom) is already
     * applied during map initialization via buildMapConfigWithSavedState().
     * @private
     */
    restoreState() {
        if (DEBUG_CBM) {
            console.debug('DEBUG: Restoring saved state...');
        }

        // Restore tile layer
        const savedLayerIndex = this.stateManager.getTileLayerIndex();
        if (savedLayerIndex >= 0 && savedLayerIndex < this.mapManager.tileSet.layers.length) {
            this.mapManager.activateTileSet(savedLayerIndex);
            // Update layer switcher UI to reflect the active layer
            if (this.mapManager.controls.tileSetSwitcher) {
                this.mapManager.controls.tileSetSwitcher.selectLayer(savedLayerIndex);
            }
        }


        this.restoreSearcherResults();
        // Note: Map view (zoom/center) is now restored during map initialization
        // via buildMapConfigWithSavedState() to avoid visual flash
    }



    /**
     * Loads persisted results from localStorage into all registered searchers.
     * Called during state restoration after all searchers are fully constructed.
     * @private
     */
    restoreSearcherResults() {
        // Load persisted results from storage now that all searchers are fully constructed
        for (const searcher of Object.values(this.searchersByName)) {
            searcher.loadResultsFromStorage();
        }
    }

    /**
     * Sets up event listeners to automatically save state on map changes.
     * Debounces view changes to avoid excessive localStorage writes during animations.
     * @private
     */
    setupStateListeners() {
        const olMap = this.mapManager.getMap();

        // Save map view (zoom and pan) when it changes
        const saveViewState = () => {
            // Debounce to avoid excessive saves during animations. The timer is on the app so destroy()
            // can cancel it; the map-manager check covers a timer that fired during a world switch anyway.
            clearTimeout(this.viewSaveTimeoutId);
            this.viewSaveTimeoutId = setTimeout(() => {
                this.viewSaveTimeoutId = null;
                if (!this.mapManager) {
                    return;
                }
                const view = this.mapManager.getView();
                this.stateManager.setMapView(view.zoom, [view.lon, view.lat]);
            }, MAPAPP_DEFAULTS.VIEW_SAVE_DEBOUNCE_MS);
        };

        olMap.getView().on('change:center', saveViewState);
        olMap.getView().on('change:resolution', saveViewState);

        // Save tile layer when it changes
        // This is handled by the layer switcher, so we'll update that component

    }













    // ==========================================
    // EVENT HANDLING
    // ==========================================


    /**
     * Callback invoked when the active tile set changes.
     * Persists the new set index to localStorage via StateManager.
     * @param {number} tileSetIndex - Index of the newly selected tile set
     */
    onTileSetChange(tileSetIndex) {
        this.stateManager.setTileLayerIndex(tileSetIndex);
    }

    /**
     * Callback invoked when the map visual effect changes.
     * Persists the new effect ID to localStorage via StateManager.
     * @param {string} effectId - ID of the newly selected effect
     */
    onMapEffectChange(effectId) {
        this.stateManager.setMapEffect(effectId);
    }

    /**
     * Callback invoked when overlay layer visibility changes.
     * Persists the visibility state to localStorage via StateManager.
     * @param {string|null} layerId - The layer id that changed, or null for toggle all
     * @param {boolean} visible - The new visibility state
     * @param {Object} allState - Current visibility state for all layers
     */
    onOverlayVisibilityChange(layerId, visible, allState) {
        this.stateManager.setAllOverlayVisibility(allState);
        // Update overlay layers visibility when any overlay state changes
        this.updateOverlayLayersVisibility();
        this.displayNeedsRefresh_AfterOverlayLayerChange();
    }


    /**
     * Gets the currently selected region.
     * @returns {string} Region ID or empty string for all regions
     */
    getSelectedRegion() {
        return this.selectedRegion;
    }
    /**
     * Returns whether the restrict-to-region checkbox is checked on the advanced searcher.
     * @returns {boolean} True if search results should be restricted to the selected region
     */
    getSelectedRegionFilterCheckbox() {
        return this.advancedSearcher.getRestrictSearchToRegion();
    }

    /**
     * Returns whether the sort-by-anchor-distance checkbox is checked on the advanced searcher.
     * @returns {boolean} True if results should be sorted by distance to anchor
     */
    getSortByAnchorDistanceCheckbox() {
        return this.advancedSearcher.getSortByAnchorDistance();
    }

    /**
     * Updates the sort-by-anchor-distance checkbox visibility.
     * Should be called whenever the anchor changes.
     */
    updateSortByAnchorDistanceCheckboxEnabledState() {
        this.advancedSearcher.updateSortByAnchorDistanceCheckboxEnabledState();
    }

    /**
     * Notifies the advanced searcher that the anchor has changed.
     */
    onAnchorChangedNotifyAdvancedSearcher() {
        this.advancedSearcher.onAnchorChanged();
    }


    /**
     * Sets the selected region programmatically.
     * Updates control UI, persists state, and refreshes visualization.
     * @param {string} regionId - Region ID or empty string for all
     * @param {boolean} flagForceCheckboxStateOn - If true, forces the restrict-to-region checkbox on
     */
    setSelectedRegionProgrammatically(regionId, flagForceCheckboxStateOn) {
        // no region is '' everywhere (the region control, this.selectedRegion and the stored state)
        regionId = (typeof regionId === 'string') ? regionId : '';
        // Update the control UI
        const regionControl = this.mapManager.getRegionControl();
        if (regionControl) {
            regionControl.setSelectedRegion(regionId);
        }
        if (flagForceCheckboxStateOn) {
            this.advancedSearcher.setRestrictSearchToRegion(true);
        }
        //
        this.doRegionSelectUpdate(regionId, false);

        // ATTN: TODO show the checkbox thing?
    }



    /**
     * Callback invoked when region selection changes from RegionControl.
     * @param {string} regionId - The newly selected region ID
     */
    onRegionChange(regionId) {
        this.doRegionSelectUpdate(regionId, true);
    }


    /**
     * Updates the region selection state, persists it, and refreshes visualization.
     * Notifies the advanced searcher if the change was user-triggered.
     * @param {string} regionId - The newly selected region ID, or empty string for all
     * @param {boolean} flagUserTrigger - True if the region change was initiated by user interaction
     */
    doRegionSelectUpdate(regionId, flagUserTrigger) {
        regionId = (typeof regionId === 'string') ? regionId : '';
        this.selectedRegion = regionId;
        this.stateManager.setSelectedRegion(regionId);
        this.refreshRegionVisualization();
        this.updateRegionOverlayVisibility();
        this.ensureVisibilityOfRegionByIdMaybe(regionId);

        // Notify AdvancedSearcher to update checkbox state and re-search if needed
        if (flagUserTrigger) {
            this.advancedSearcher.onRegionChanged();
        }

        this.displayNeedsRefresh_AfterRegionSelect();
    }


    /**
     * Refreshes the region visualization on the map.
     * Draws region outline or inverted mask depending on settings.
     */
    refreshRegionVisualization() {
        let geometry = null;
        if (this.selectedRegion) {
            const regionData = this.auxDataManager.getRegionData();
            if (regionData) {
                const selectedRegionRow = regionData.find(r => r.jregion === this.selectedRegion);
                if (selectedRegionRow && selectedRegionRow.geometry) {
                    geometry = selectedRegionRow.geometry;
                }
            }
        }

        const outsideRegionOpacity = this.stateManager.getOutsideRegionOpacity();
        this.mapManager.updateRegionLayerStyle(geometry, outsideRegionOpacity);
    }






    // ==========================================
    // COMMAND HANDLING
    // ==========================================


    /**
     * Handles application commands triggered from the help system or other UI elements.
     * Currently supports 'goSettings' to open the settings dialog.
     * @param {string} command - Command identifier (e.g., 'goSettings')
     * @param {string} param - Optional parameter for the command
     * @param {Window} window - Window context (unused)
     * @returns {boolean|undefined} True if command was handled
     */
    handleAppCommand(command, param) {
        if (command == 'goSettings') {
            // Open settings dialog
            this.uiManager.handleSettingsClick();
            return true;
        } else if (command == 'search') {
            // Open settings dialog
            this.handleAppCommandSearch(param);
            return true;
        } else {
            console.error('ERROR: Unknown App command received (typically via help link):', command, param);
        }
    }


    /**
     * Handles a search command with format TYPE:query.
     * Parses the parameter and triggers the appropriate search.
     * @param {string} param - Search parameter in format "TYPE:query" (e.g., "white:Smith")
     * @param {Window} window - Window context (unused)
     */
    handleAppCommandSearch(param) {
        const colonIndex = param.indexOf(':');
        if (colonIndex === -1) {
            console.error(`ERROR: handleAppCommandSearch: invalid param format, expected TYPE:query, got: "${param}"`);
            return;
        }
        const searchType = param.substring(0, colonIndex);
        const searchQuery = param.substring(colonIndex + 1);
        if (DEBUG_CBM) {
            console.debug(`DEBUG: handleAppCommandSearch with param ${param} and type=${searchType} and query = ${searchQuery}`);
        }
        this.programmaticallyRunSearchOnQuery(searchType, searchQuery, false);
    }



    /**
     * Executes a search programmatically from a saved search item.
     * Builds a search pack from the saved item's properties and runs it.
     * @param {Object} searchItem - Saved search item with searchQuery, searchType, selectedRegion, and regionCheckbox
     */
    programmaticallyRunSearchOnSavedItem(searchItem) {
        // console.log(`DEBUG: In programmaticallyRunSearchOnSavedItem "${JSON.stringify(searchItem)}"`)
        // we build a search pack so we can save selectedRegion
        const searchPack = {
            query: searchItem.searchQuery,
            queryOriginal: searchItem.searchQuery,
            searchType: searchItem.searchType,
            isEnterKey: true,
            flagUserInitiatedThisSearch: true,
            selectedRegion: searchItem.selectedRegion,
            regionCheckbox: searchItem.regionCheckbox,
        }
        this.programmaticallyRunSearchOnSearchPack(searchItem.searchType, searchPack);
    }

    /**
     * Executes a search programmatically by query string, switching to the appropriate tab first.
     * Builds a search pack from the query and delegates to programmaticallyRunSearchOnSearchPack.
     * @param {string} searcherName - Name of the searcher/tab to run the search on (see SEARCH_TYPES)
     * @param {string} searchQuery - The search query string
     */
    programmaticallyRunSearchOnQuery(searcherName, searchQuery, flagFromMapClick) {
        const searchPack = buildSearchPackFromQuery(searchQuery, searcherName, true, true, flagFromMapClick);
        this.programmaticallyRunSearchOnSearchPack(searcherName, searchPack);
    }

    /**
     * Executes a search programmatically using a pre-built search pack, switching to the appropriate tab first.
     * Used for triggering searches from history, bookmarks, or "See Also" links.
     * @param {string} searcherName - Name of the searcher/tab to run the search on (see SEARCH_TYPES)
     * @param {Object} searchPack - Search pack object containing query, options, and metadata
     */
    programmaticallyRunSearchOnSearchPack(searcherName, searchPack) {
        // (help links reach this from the app-scoped help viewer; there are no searchers without a world)
        if (!this.isWorldReady()) {
            return;
        }
        // Switch to the appropriate tab first
        this.switchToTabBySearcherName(searcherName);

        const searcher = this.getSearcherByName(searcherName);
        if (searcher) {
            // Update the input field
            searcher.setInputValuesFromSearchPack(searchPack);
            // Run the search
            searcher.performSearch(searchPack);
        }
    }




















    // ==========================================
    // SavedSearcher proxy functions just pass on the functions to bookmarkSearcher
    // ==========================================




    /**
     * Checks if a search is saved by UID.
     * @param {string} uid - Unique identifier to check
     * @returns {boolean} True if the search is saved
     */
    isSavedSearchByUid(uid) {
        return this.savedSearcher.isSavedSearchByUid(uid);
    }



    // ==========================================
    // bookmarkSearcher proxy functions just pass on the functions to bookmark searcher
    // ==========================================



    /**
     * Checks if a POI is bookmarked by UID.
     * @param {string} uid - Unique identifier to check
     * @returns {boolean} True if the POI is bookmarked
     */
    isBookmarkedByUid(uid) {
        return this.bookmarkSearcher.isBookmarkedByUid(uid);
    }



    // ==========================================
    // currentAnchor accessors and actions
    // ==========================================

    /**
     * Gets the current anchor object.
     * @returns {Object} The current anchor with uid and xy properties
     */
    getCurrentAnchor() {
        return this.currentAnchor;
    }

    /**
     * Sets the current anchor to a specific POI.
     * @param {string} uid - Unique identifier of the POI to anchor
     * @param {Array<number>} xy - Coordinates [x, y] for the anchor position
     * @param {Object} item - The full POI item data for rendering
     */
    setCurrentAnchor(uid, xy, item) {
        this.currentAnchor = { uid, xy, item };
        this.refreshAnchorMarkerAfterChange();
        this.onAnchorChangedNotifyAdvancedSearcher();
    }

    /**
     * Clears the current anchor.
     */
    clearCurrentAnchor() {
        this.currentAnchor = { uid: null, xy: null, item: null };
        this.refreshAnchorMarkerAfterChange();
        this.onAnchorChangedNotifyAdvancedSearcher();
    }

    /**
     * Checks if a POI is currently anchored by UID.
     * @param {string} uid - Unique identifier to check
     * @returns {boolean} True if the POI is anchored
     */
    isAnchoredByUid(uid) {
        return this.currentAnchor.uid === uid;
    }



    /**
     * Refreshes the anchor marker display on the map.
     * Updates both the anchor ring layer and the anchor POI marker layer.
     * Called when anchor state changes.
     */
    refreshAnchorMarkerAfterChange() {
        if (!this.isInitialized()) {
            return;
        }
        const { uid, xy, item } = this.currentAnchor;
        // Update the anchor ring layer
        this.mapManager.updateAnchorLayerAll(uid, xy, item);
        // Update visibility based on whether anchor is already shown elsewhere
        this.mapManager.updateAnchorPoiVisibility();
        // re-perform search
        this.displayNeedsRefresh_AfterAnchorChanged(uid, item);
    }


    /**
     * Returns whether results should be sorted by distance to the anchor.
     * True only when the sort-by-anchor-distance checkbox is checked and an anchor is set.
     * @returns {boolean} True if results should be sorted by anchor distance
     */
    calcShouldSortByAnchorDist() {
        const shouldSortByAnchorDist = this.advancedSearcher.getSortByAnchorDistance() && this.getCurrentAnchor().uid;
        return shouldSortByAnchorDist;
    }

    // ==========================================
    // Helper for ActionBar icons
    // ==========================================


    /**
     * Checks if a POI item has custom edited properties (notes, comments).
     * Used for actionbar icon display decisions.
     * @param {string} uid - Unique identifier of the item
     * @param {Object} item - The POI item to check
     * @returns {boolean} True if the item has custom edited properties
     */
    hasCustomEditedPropertiesByUid(uid, item) {
        if (!item) {
            // this can happen if item is in process of being deleted
            return false;
        }
        // return true if this item has been custom edited (ie has custom notes or comment)
        return (item.isCustomEdited ? true : false);
    }




    // ==========================================
    // Helper for Menus on search bars
    // ==========================================


    /**
     * Initializes event listeners for search bar dropdown menu actions.
     * Attaches click handlers to all dropdown items in searchTabsContent.
     */
    initializeSearchBarMenuActionListeners() {
        // the dropdown items are static DOM: bind once per page (see constructor), or a world switch
        // would run every menu action (export, clear, ...) once per world loaded so far
        if (this.searchBarMenuListenersInstalled) return;
        this.searchBarMenuListenersInstalled = true;

        const searchTabsContent = document.getElementById(DOM_ELEMENTS.ID_SEARCH_TABS_CONTENT);
        if (!searchTabsContent) {
            console.error(`ERROR: ${DOM_ELEMENTS.ID_SEARCH_TABS_CONTENT} not found`);
            return;
        }

        // Find all dropdown menu items within searchTabsContent
        // Menu buttons follow the pattern: NAME-menu-btn (e.g., white-menu-btn, yellow-menu-btn)
        // Menu action items follow the pattern: NAME-menu-ACTION (e.g., white-menu-help, yellow-menu-export)
        const menuItems = searchTabsContent.querySelectorAll('.dropdown-menu .dropdown-item');

        menuItems.forEach(item => {
            item.addEventListener('click', (e) => {
                e.preventDefault();
                const menuActionId = item.id;
                if (menuActionId) {
                    this.handleSearchBarMenuAction(menuActionId);
                }
            });
        });
    }

    /**
     * Handles a search bar menu action triggered by clicking a dropdown item.
     * @param {string} menuActionId - The ID of the menu action (e.g., 'white-menu-help', 'yellow-menu-export')
     */
    handleSearchBarMenuAction(menuActionId) {
        // Parse the menuActionId: format is {tabName}-menu-{action}
        // e.g., 'white-menu-help' -> tabName='white', action='help'
        // e.g., 'savedsearch-menu-import' -> tabName='savedsearch', action='import'
        const menuMarker = '-menu-';
        const menuIndex = menuActionId.indexOf(menuMarker);

        if (menuIndex === -1) {
            console.error('ERROR: Invalid menu action ID format:', menuActionId);
            return;
        }

        // parse menu parts
        const tabName = menuActionId.substring(0, menuIndex);
        const action = menuActionId.substring(menuIndex + menuMarker.length);

        // the searcher actions need the world's searchers (the menus themselves are app-scoped DOM)
        const needsSearcher = (tabName !== SEARCH_TYPES.LOGPLAY) && ['export', 'import', 'clearAll', 'saveSearch', 'makeAdvanced'].includes(action);
        if (needsSearcher && (!this.isWorldReady() || !this.getSearcherByName(tabName))) {
            showStatusWarning('No world is loaded right now; pick a world (or wait for it to finish loading) first.');
            return;
        }

        // run the actions
        if (action=='help') {
            let topicId;
            if ([SEARCH_TYPES.WHITE, SEARCH_TYPES.YELLOW, SEARCH_TYPES.REVERSE, SEARCH_TYPES.ADVANCED].includes(tabName)) {
                topicId = 'search-' + tabName;
            } else {
                topicId = tabName;
            }
            // trigger help
            this.uiManager.handleHelpClick(topicId);
        } else if ((action=='export') && (tabName == 'logplay')) {
            // export LOG
            this.playLogger.saveLogInteratively();
        } else if (action=='export') {
            // export results -- this is the most general
            const label = `${tabName} data`;
            const defaultFilename = `${tabName}_data`;
            const searcher = this.getSearcherByName(tabName);
            const data = searcher.getInternalFilteredResultsIfAppropriate();
            this.exportInteractively(tabName, label, defaultFilename, data, 'json');
        } else if (action=='import') {
            // export results -- this is the most general
            const label = `${tabName} data`;
            const searcher = this.getSearcherByName(tabName);
            // console.log(`attn: active searcher is "${tabName}".`)
            const flagSupportsImportLeadsFromPdf = searcher.getSupportsImportLeadsFromPdf();
            this.importInteractively(tabName, label, flagSupportsImportLeadsFromPdf);
        } else if (action=='copy' && tabName === 'logplay') {
            this.playLogger.copyPlayLogToClipboard();
        } else if (action=='copyStarLines' && tabName === 'logplay') {
            this.playLogger.copyStarLinesToClipboard();
        } else if (action=='clearAll' && tabName === 'logplay') {
            this.playLogger.clearPlayLog();
        } else if (action=='clearAll') {
            // delete results -- this is the most general
            const searcher = this.getSearcherByName(tabName);
            searcher.confirmThenClearAllResults();
        } else if (action=='saveSearch') {
            const searcher = this.getSearcherByName(tabName);
            searcher.saveLastSearchToSavedSearcher();
        } else if (action=='makeAdvanced') {
            const searcher = this.getSearcherByName(tabName);
            searcher.makeAdvancedSearch();
        } else if (action=='visitNotebookLm') {
            window.open(WEB_URLS.ADVANCED_SEARCH_NOTEBOOK_LM, '_blank');
        } else {
            console.error(`ERROR: Unknown tab menu actions "${action}".`)
        }
    }















    // ==========================================
    // NEW MAP INTERACTIONS
    // ==========================================

    /**
     * Called by OpenLayersMapManager when user clicks on a map marker.
     * Navigates to and selects the corresponding result item in the sidebar.
     * @param {string} uid - Unique identifier of the clicked marker's POI
     */
    userClicksOnMapMarkerItemByUId(uid) {
        // go to it on the searcher(s) result lists
        // This will eventually end up calling onItemSelected, but we pass true as second parameter so we know that not to recursively go to this result on the map
        this.someoneElseInstructsGoToAndSelectResultItemByUid(uid, false);
    }


    /**
     * Called when the user clicks on a space on the map that does not belong to a visible marker.
     * Identifies the region/block at that location and performs a reverse search.
     * @param {Array<number>} geoPoint - [x, y] coordinates of the clicked point
     */
    userClicksOnNonMarkerPointOnMap(geoPoint) {
        const regionBlockString = this.identifySelectedRegionBlockStringFromGeoPoint(geoPoint, MAP_CONSTANTS.MAX_DIST_FOR_CLICK_SEARCH);
        if (regionBlockString) {
            this.programmaticallyRunSearchOnQuery(SEARCH_TYPES.REVERSE, regionBlockString, true);
        } else {
            // showStatusError('Could not determine the block you are indicating.');
            this.clearCurrentSelection();
        }
    }



    /**
     * Clears the currently selected/expanded item on the active tab's lister.
     */
    clearCurrentSelection() {
        // just clear any currently expanded item
        const activeSearcher = this.calcActiveSearcherReference();
        if (activeSearcher) {
            activeSearcher.clearCurrentSearcherSelection();
        }
    }



















    // ==========================================
    // HELPER METHODS
    // ==========================================








    // ==========================================
    // ACCESSOR-LIKE OPERATIONS
    // ==========================================



    /**
     * Calculates the name of the active searcher based on the currently active tab.
     * @returns {string|null} Searcher name (see SEARCH_TYPES constant)
     */
    calcActiveSearcherName() {
        // get it live
        const activeTabId = this.getActiveTabIdFromDom();

        // an alternate way to get the activeTabId would be:
        // getting this from stateManager rather than getting it LIVE
        // activeTabId = this.stateManager.getSidebarTab(); // e.g., 'white-tab'

        if (!activeTabId) return null;
        const activeSearcherName = activeTabId.replace('-tab', ''); // e.g., 'white'
        return activeSearcherName;
    }

    /**
     * Calculates the name of the active searcher based on the currently active tab.
     * @returns {string|null} Searcher name (see SEARCH_TYPES constant)
     */
    calcActiveSearcherReference() {
        // get it live
        const activeSearcherName = this.calcActiveSearcherName();
        return this.getSearcherByName(activeSearcherName);
    }

    /**
     * Gets the currently active tab ID directly from the DOM.
     * @returns {string|null} The active tab ID (e.g., 'white-tab'), or null if not found
     */
    getActiveTabIdFromDom() {
        const activeTab = document.querySelector('#' + ID_SEARCH_TABS + ' .nav-link.active');
        return activeTab ? activeTab.id : null;
    }


    /**
     * Switches the active sidebar tab to match the given searcher name.
     * @param {string} searcherName - Searcher name (see SEARCH_TYPES constant)
     */
    switchToTabBySearcherName(searcherName) {
        const tabId = searcherName + '-tab';
        switchToBootstrapTabById(tabId);
    }



    /**
     * Gets the URL to a yellow page advertisement image for a business.
     * @param {string} dName - Business display name
     * @returns {string|null} URL to the ad image, or null if none exists
     */
    getUrlToYellowAdFromDname(dName) {
        const relPath = this.auxDataManager.getYellowAdUsageDictForName(dName);
        if (!relPath) {
            return null;
        }
        const url = this.auxDataManager.getYellowAdBasePath() + '/' + relPath;
        return url;
    }












    // ==========================================
    // Action triggers on results, called by clicks on result actionbar buttons, etc.
    // ==========================================

    /**
     * Toggles the anchor state of a POI item.
     * @param {string} uid - Unique identifier of the item
     * @param {Object} item - The item data
     * @param {Lister} listerp - The Lister instance
     * @returns {boolean} True if now anchored, false if unanchored
     */
    actionOnItemToggleAnchor(uid, item, listerp) {
        if (!item) item = listerp.getResultById(uid);
        // (clearCurrentAnchor / setCurrentAnchor already run displayNeedsRefresh_AfterAnchorChanged, via
        // refreshAnchorMarkerAfterChange; calling it again here re-ran the active tab's search twice)
        if (this.isAnchoredByUid(uid)) {
            // Currently anchored - clear it
            this.clearCurrentAnchor();
            // (the refresh inside clearCurrentAnchor only knows the new, empty anchor, so update the
            // action bar icons of the item that just lost it)
            this.updatePartialAppearanceOfItemByUidOnAllSearchers(uid);
            // turn off active icon state
            return false;
        }
        // Not anchored - set it, if it has a point to measure from: an entry with no location (an
        // unlisted address) made every card read "0 feet from anchor" and nearAnchor() match the
        // whole directory (second code study 12.3, Oct 2026). The list offers no anchor button for
        // one; this covers any other way in.
        if (!itemHasPointGeometry(item)) {
            showStatusWarning("This entry has no location on the map, so it cannot be the anchor.");
            return false;
        }
        const xy = item?.geometry?.coordinates || null;
        this.setCurrentAnchor(uid, xy, item);
        // it is now shown as actively anchor
        return true;
    }



    /**
     * Handles clipboard/export action on a result item.
     * Opens export dialog with the single item for copying.
     * @param {string} uid - Unique identifier of the item
     * @param {Object} item - The item data
     * @param {Lister} listerp - The Lister instance
     */
    actionOnItemCopyToClipboard(uid, item, listerp) {
        const itemLabel = item.label || item.dName;
        const label = `${itemLabel}`;
        const defaultFilename = `singleItem`;
        const data = [item];
        this.exportInteractively(label, label, defaultFilename, data, 'casebook');
    }

    /**
     * Handles launch search action on a saved search item.
     * Executes the saved search query.
     * @param {string} uid - Unique identifier of the search item
     * @param {Object} item - The search item data
     * @param {Lister} listerp - The Lister instance
     * @returns {boolean} True if search was launched successfully
     */
    actionOnItemLaunchSearch(uid, item, listerp) {
        //console.log(`In actionOnItemLaunchSearch with item = "${JSON.stringify(item)}" with uid = ${uid}`);
        if (!item) item = listerp.getResultById(uid);
        // do we need to check this??
        if (item.ptype == PTYPES.SEARCH) {
            this.programmaticallyRunSearchOnSavedItem(item);
            return true;
        }
        return false;
    }



    /**
     * Handles delete action on a result item.
     * Removes the item from its lister and optionally adds to history for undo.
     * @param {string} uid - Unique identifier of the item
     * @param {Object} item - The item data
     * @param {Lister} listerp - The Lister instance
     * @returns {boolean} True if deletion was successful
     */
    actionOnItemDelete(uid, item, listerp) {
        // remove it
        const searcher = listerp.getSearcher();
        if (uid) {
            // FIRST add it to history if appropriate! this creates a kind of UNDO remove from trash if user makes a mistake
            this.addSelectedSearchResultIfAppropriateToHistory(listerp, item, true);
            // now remove it -- this is a real delete that we may need to do other work on, so delegate to search
            const wasCustomized = item.isCustomized;
            searcher.removeItemByUidItem(uid, item, true);
            const isCustomized = item.isCustomized;
            // knock-on -- note we pass (wasCustomized != isCustomized) expression will only be true if we are deleting from bookmark searcher and this could have knock-on effects
            this.displayNeedsRefresh_AfterItemDelete(uid, item, searcher, wasCustomized != isCustomized);

            return true;
        }
        return false;
    }


    /**
     * Handles read lead
     * @param {string} uid - Unique identifier of the item
     * @param {Object} item - The item data
     * @param {Lister} listerp - The Lister instance
     */
    async actionOnItemReadLead(uid, item, listerp) {
        const leadId = item.lead;
        const retv = await this.goToLead(item, leadId);
        if (!retv) {
            // The lead wasn't found in the loaded book (or no book is loaded); still record it
            // in History so the user has a record of having looked it up and can return to it.
            const flagIncludeSearches = false;
            this.addSelectedSearchResultIfAppropriateToHistory(listerp, item, flagIncludeSearches);
        }
    }




    /**
     * Handles extra actions menu - shows a Bootstrap dropdown anchored to the clicked button.
     * @param {string} uid - Unique identifier of the item
     * @param {Object} item - The item data
     * @param {Lister} listerp - The Lister instance
     * @param {Event} event - The click event from the action button
     */
    actionOnItemExtraActionsMenu(uid, item, listerp, event) {
        const actions = [
            // (1940s.nyc covers New York City only: offered for a place inside it, isItemInNewYorkCity)
            ...(this.isItemInNewYorkCity(item) ? [{ id: 'geolocate1940s', label: 'Geolocate at 1940s.nyc', icon: 'bi-geo-alt' }] : []),
            { id: 'geolocateGoogle', label: 'Geolocate on Google Maps', icon: 'bi-geo-alt-fill' },
            { id: 'geolocateArcgis', label: 'Geolocate at ArcGIS aerial map', icon: 'bi-geo-alt' },
        ];
        const actionHandlers = {
            geolocate1940s: (uid, item) => this.externalGeoLocate1940sNyc(uid, item),
            geolocateGoogle: (uid, item) => this.externalGeoLocateGoogleMaps(uid, item),
            geolocateArcgis: (uid, item) => this.externalGeoLocateArcgisAerial(uid, item),
        };
        this.showExtraActionsDropdown(event, actions, (actionId) => {
            const handler = actionHandlers[actionId];
            if (handler) {
                handler(uid, item);
            }
        });
    }


    /**
     * Creates and shows a Bootstrap dropdown menu anchored to the clicked button.
     * @param {Event} event - The click event
     * @param {Array<{id: string, label: string, icon?: string}>} actions - Menu items
     * @param {Function} onSelect - Callback receiving the selected action id
     */
    showExtraActionsDropdown(event, actions, onSelect) {
        // Close any existing extra-actions dropdown (through its own close, so its document listener goes too)
        if (this.closeExtraActionsDropdown) {
            this.closeExtraActionsDropdown();
        }
        document.querySelector('.extra-actions-dropdown')?.remove();

        const button = event.currentTarget;

        // Build the dropdown menu
        const wrapper = document.createElement('div');
        wrapper.className = 'dropdown extra-actions-dropdown';
        wrapper.style.position = 'absolute';
        wrapper.style.zIndex = '1080';

        let menuHtml = '<ul class="dropdown-menu dropdown-menu-end show">';
        for (const action of actions) {
            const iconHtml = action.icon ? `<i class="bi ${action.icon} me-2"></i>` : '';
            menuHtml += `<li><a class="dropdown-item" href="#" data-action-id="${action.id}">${iconHtml}${action.label}</a></li>`;
        }
        menuHtml += '</ul>';
        wrapper.innerHTML = menuHtml;

        // Position near the button
        document.body.appendChild(wrapper);
        const buttonRect = button.getBoundingClientRect();
        wrapper.style.top = `${buttonRect.bottom + window.scrollY}px`;
        wrapper.style.left = `${buttonRect.right + window.scrollX}px`;
        // Adjust so menu aligns right edge to button right edge
        const menuEl = wrapper.querySelector('.dropdown-menu');
        requestAnimationFrame(() => {
            const menuWidth = menuEl.offsetWidth;
            wrapper.style.left = `${buttonRect.right + window.scrollX - menuWidth}px`;
        });

        // ONE close path for every way the menu goes away (item chosen, outside click, another menu
        // opened): it removes the menu AND the capture-phase document listener, which used to stay
        // attached until the next click whenever the menu closed through an item
        let closed = false;
        let outsideClickHandlerInstalled = false;
        const closeHandler = (e) => {
            if (!wrapper.contains(e.target)) {
                closeMenu();
            }
        };
        const closeMenu = () => {
            if (closed) return;
            closed = true;
            wrapper.remove();
            if (outsideClickHandlerInstalled) {
                document.removeEventListener('click', closeHandler, true);
                outsideClickHandlerInstalled = false;
            }
            if (this.closeExtraActionsDropdown === closeMenu) {
                this.closeExtraActionsDropdown = null;
            }
        };
        this.closeExtraActionsDropdown = closeMenu;

        // Handle item click
        menuEl.addEventListener('click', (e) => {
            const link = e.target.closest('[data-action-id]');
            if (link) {
                e.preventDefault();
                const actionId = link.dataset.actionId;
                closeMenu();
                onSelect(actionId);
            }
        });

        // Close on outside click. Deferred so this click event doesn't immediately close it (and
        // skipped if the menu was already closed in the meantime).
        setTimeout(() => {
            if (closed) return;
            document.addEventListener('click', closeHandler, true);
            outsideClickHandlerInstalled = true;
        }, 0);
    }
















    /**
     * Conditionally adds a selected result to history if it's a POI item.
     * Skips adding if the item is a search type or if this searcher is the history searcher.
     * @param {Lister} listerp - The Lister the item was selected in
     * @param {Object} item - The result item
     * @param {boolean} flagIncludeSearches - Whether to include search-type items (there is no uid parameter)
     * @returns {boolean} True if item was added to history
     */
    addSelectedSearchResultIfAppropriateToHistory(listerp, item, flagIncludeSearches) {
        const ptype = item.ptype;
        if ((!flagIncludeSearches) && (ptype == PTYPES.SEARCH)) {
            // we dont RE-add searches to history
            return false;
        }

        const historySearcher = this.getHistorySearcher();
        if (listerp.parentSearcher == historySearcher) {
            // dont re-add!
            return false;
        }

        // note it could still be a duplicate but we will let history searcher worry about that

        // ok add it
        historySearcher.addHistoryItemDirect(item);

        return true;
    }






















//
// ACTIONBAR TOGGLES should return true if actionbar button state is active, false if not
//








    /**
     * Toggles the bookmark state of a POI item.
     * @param {string} uid - Unique identifier of the item
     * @param {Object} item - The item data
     * @param {Lister} listerp - The Lister instance
     * @returns {boolean} True if now bookmarked, false if unbookmarked
     */
    actionOnItemToggleBookmark(uid, item, listerp) {
        if (!item) item = listerp.getResultById(uid);
        const wasCustomized = item.isCustomized;
        const retv = this.bookmarkSearcher.addOrRemoveBookmarkByUidItem(uid, item);
        const isCustomized = item.isCustomized;

        const flagPossibleMajorChange = wasCustomized != isCustomized;
        this.displayNeedsRefresh_AfterBookmarkStatusChanged(uid, flagPossibleMajorChange);
    
        // return true if we should show icon as active
        if (retv) {
            return true;
        }
        return false;
    }

    /**
     * Toggles the saved search state of a search item.
     * @param {string} uid - Unique identifier of the search
     * @param {Object} item - The search item data
     * @param {Lister} listerp - The Lister instance
     * @returns {boolean} True if now saved, false if unsaved
     */
    actionOnItemToggleSavedSearch(uid, item, listerp) {
        if (!item) item = listerp.getResultById(uid);
        const retv =  this.savedSearcher.addOrRemoveSavedSearchByUidItem(uid, item);
        this.displayNeedsRefresh_AfterSavedSearchStatusChanged(uid);

        // return true if we should show icon as active
        if (retv) {
            return true;
        }
        return false;
    }



    /**
     * Handles launch/go-to action on a POI item; this should trigger a ZOOM TO
     * @param {string} uid - Unique identifier of the item
     * @param {Object} item - The item data
     * @param {Lister} listerp - The Lister instance
    */
    actionOnItemLaunchItem(uid, item, listerp) {
        this.ensureMapTabVisible();
        this.ensureVisibleMaybeZoomToPoiByUid(uid, item, true, false)
        // no change of icon
        return false;
    }


    /**
     * Handles edit action on a result item.
     * @param {string} uid - Unique identifier of the item
     * @param {Object} item - The item data
     * @param {Lister} listerp - The Lister instance
     */
    async actionOnItemEdit(uid, item, listerp) {
        // called function will trigger display updates
        await this.editItemCreateBookmark(uid, item, listerp);
        return item.isCustomized;
    }


    /**
     * Opens the BookmarkDialog for editing an item's custom properties and processes the result.
     * If the user saves, delegates to doSaveEditedItem to persist changes.
     * @async
     * @param {string} uid - Unique identifier of the item
     * @param {Object} item - The POI item to edit
     * @param {Lister} listerp - The Lister instance where the edit was triggered
     * @returns {Promise<boolean>} True if the user saved changes, false if cancelled
     */
    async editItemCreateBookmark(uid, item, listerp) {
        const result = await BookmarkDialog.showBookmarkDialog({
            mapApp: this,
            item: item
        });

        // console.log(`BACK FROM AWAIT BOOKMARK SAVE WITH ${JSON.stringify(result)}`)

        let retv;
        if (result?.action === 'save') {
            // this will trigger displayNeedsRefresh_AfterResultItemAddOrDelete(uid) to refresh display of this item;
            this.doSaveEditedItem(item, result, listerp);
            this.displayNeedsRefresh_AfterCustomEditItem(uid);
            retv = true;
        } else {
            // we still need an update? just for the actionbar icon? no
            retv = false;
        }

        return retv;
    }






























    // ==========================================
    // Import / Export delegation to ImportExportHelper
    // ==========================================


    /**
     * Opens the import dialog and handles interactive data import.
     * Delegates to ImportExportHelper.
     * @async
     * @param {string} tabName - Target tab/searcher name for import
     * @param {string} label - Display label for the import dialog
     * @returns {Promise<boolean>} True if import was successful
     */
    async importInteractively(tabName, label, flagSupportsImportLeadsFromPdf) {
        return this.importExportHelper.importInteractively(tabName, label, flagSupportsImportLeadsFromPdf);
    }


    /**
     * Opens the export dialog and handles interactive data export.
     * Delegates to ImportExportHelper.
     * @async
     * @param {string} tabName - Source tab/searcher name
     * @param {string} label - Display label for the export dialog
     * @param {string} defaultFilename - Default filename without extension
     * @param {Array<Object>} dataAsJson - Data to export as JSON array
     * @param {string} defaultFormat - Default export format ('json', 'casebook', 'plaintext')
     * @returns {Promise<void>}
     */
    async exportInteractively(tabName, label, defaultFilename, dataAsJson, defaultFormat) {
        return this.importExportHelper.exportInteractively(tabName, label, defaultFilename, dataAsJson, defaultFormat);
    }









    // ==========================================
    // Helpers for clicking on map
    // ==========================================

    /**
     * This function will be passed a geo map location from the OpenLayersMapManager class when the user clicks on a point on the map where there is no visible marker.
     * This function will first find a row in this.blockData which has an enclosing Polygon geometry
     * If a row with an enclosing polygon geometry in blockdata was not found, then find the row with the closest polygon to the point.
     * With this blockdata row, we then find the nearest row in this.locData whose blockid matches the blockid of the blockData row.
     * Then return a string which is the locData row jregion field concattenated with the locData row jlabel field
     * If no such locData row was found return ""
     *
     * @param {Array<number>} geoPoint - [x, y] coordinates of the clicked point
     * @returns {string} Region and block label string, or empty string if not found
     */
    identifySelectedRegionBlockStringFromGeoPoint(geoPoint, maxDist) {
        const blockRegion = this.auxDataManager.identifySelectedRegionBlockStringFromGeoPointViaBlockData(geoPoint, maxDist);
        if (blockRegion) {
            return blockRegion;
        }

        // fall back to nearest poi
        if (DEBUG_CBM) {
            console.debug('DEBUG: identifySelectedRegionBlockStringFromGeoPoint - Falling back to nearest poi block.');
        }
        return this.identifySelectedRegionBlockStringFromGeoPointViaNearestPoi(geoPoint, maxDist);
    }



    /**
     * This function will be passed a geo map location by the OpenLayersMapManager class when the user clicks on a point on the map where there is no visible marker.
     * This function will first find the nearest point row in combined data and return that poi's jregionblock.
     * If no such data row was found return ""
     *
     * @param {Array<number>} geoPoint - [x, y] coordinates of the clicked point
     * @returns {string} Region and block label string, or empty string if not found
     */
    identifySelectedRegionBlockStringFromGeoPointViaNearestPoi(geoPoint, maxDist) {

        if (!this.poiDataManager || !this.poiDataManager.isLoaded()) {
            return "";
        }
        const dataCombined = this.poiDataManager.getDataCombined();
        if (!dataCombined || dataCombined.length === 0) {
            return "";
        }

        const [px, py] = geoPoint;

        // Find the nearest point in dataCombined
        let nearestItem = null;
        let minDistanceSquared = Infinity;

        for (const item of dataCombined) {
            if (!item.geometry || !item.geometry.coordinates) {
                continue;
            }

            const distanceSquared = distanceSquaredToCoords(px, py, item.geometry.coordinates);

            if (distanceSquared < minDistanceSquared) {
                minDistanceSquared = distanceSquared;
                nearestItem = item;
            }
        }

        if (!nearestItem || !nearestItem.jregion) {
            return "";
        }

        if (Math.sqrt(minDistanceSquared) > maxDist) {
            return "";
        }

        // Return concatenated jregion and loclabel
        const loclabel = nearestItem.loclabel || "";
        return `${nearestItem.jregion}${loclabel}`;
    }








    // ==========================================
    // Various ways to fit extent; these may be triggered from map buttons
    // ==========================================

    /**
     * Resets the map view to the initial bounds (first tile layer bounds scaled to 80%).
     */
    fitBoundsInitial() {
        if (this.isInitialized()) {
            this.mapManager.resetToFitBoundsInitial();
        }
    }






























    // ==========================================
    // Delayed Messages
    // ==========================================

    /**
     * Clears any pending delayed search save timer.
     * Called at the start of a new search to cancel pending auto-save on typing.
     * @override
     */
    clearPendingDelayedSearchSave() {
        if (this.delayedSearchSaveTimeoutId) {
            clearTimeout(this.delayedSearchSaveTimeoutId);
            this.delayedSearchSaveTimeoutId = null;
        }
    }

    /**
     * Schedules a delayed save of the search query to history.
     * Allows history update even when user doesn't press Enter.
     * @param {string} query - The search query
     */
    scheduleDelayedSearchSave(searcherp, searchPack) {
        // Clear any prior
        this.clearPendingDelayedSearchSave();

        // If blank do nothing
        if (!searchPack.query) return;

        // Schedule it
        this.delayedSearchSaveTimeoutId = setTimeout(() => {
            this.triggerDelayedSearchSave(searcherp, searchPack);
        }, MAPAPP_DEFAULTS.LIVE_SEARCH_SAVE_DELAY_MS);
    }

    /**
     * Triggers the delayed search save, clearing the timer and executing the save.
     * @param {string} searchPack
     */
    triggerDelayedSearchSave(searcherp, searchPack) {
        this.clearPendingDelayedSearchSave();
        const debugMessage = `triggering delayed search save to history..`;
        searcherp.doSaveSearchQuery(this.getHistorySearcher(), searchPack, debugMessage);
        this.logPlaySearchRun(searchPack);
    }










    // ==========================================
    // On Triggers that might adjust highlight marker ring
    // ATTN: THESE ARE THE CURRENT WAY WE DO IT AND WE WANT TO CHANGE IT
    // ==========================================


    /**
     * Called when an item is EXPANDED which is synonymous in our system with HIGHLIGHTED
     * @param {Object} item - The selected result item data
     * @param {string} uid - The unique identifier of the selected result
     * @param {Object} lister - The Lister instance where the selection occurred
    */
    onItemExpand(item, uid, lister, flagFlashMarker) {
        // We ignore the details and just tell the highlight ring marker to update; it can figure out *how* to update
        //console.log(`ATTN: IN onItemExpand ${uid}`);
        // ATTN: i dont think we have to call this here, as it will be called when we refresh the selected item
        // i think this is handled in onItemSelected
        if (true) {
            this.refreshHighlightedRingMarker(flagFlashMarker, flagFlashMarker, 'onExpand');
        }
    }

    /**
     * Called when an item is COLLAPSED which is synonymous in our system with UN-HIGHLIGHTED
     * @param {Object} item - The selected result item data
     * @param {string} uid - The unique identifier of the selected result
     * @param {Object} lister - The Lister instance where the selection occurred
    */
    onItemCollapse(lister) {
        // We ignore the details and just tell the highlight ring marker to update; it can figure out *how* to update
        //console.log(`ATTN: IN onItemCollapse`);
        // ATTN: i dont think we have to call this here, as it will be called when we refresh the selected item
        // ATTN: we *DO* need to call this in some cases when its not a new one being selected
        if (true) {
            this.refreshHighlightedRingMarker(false, false, 'onItemCollapse');
        }
        // ATTN: This is the end of the chain of calls when a result is unselected/unhighlighted, so now we invoke our helpers for ripple on effects
        this.displayNeedsRefresh_AfterHighlightStatusChange(null, null, false);
    }


    /**
     * Called when a result item is selected in any lister.
     * This is the CENTRAL MAPAPP callback for result selection across the application.
     * Override or extend this method to add custom behavior when results are selected.
     *
     * @param {string} uid - The unique identifier of the selected result
     * @param {Object} item - The selected result item data
     * @param {Object} lister - The Lister instance where the selection occurred
     * @param {boolean} fromMapMarkerClick - True if selection was triggered by clicking a map marker,
     *                                       false if triggered by clicking in a result list
     */
    onItemSelected(uid, item, lister, fromMapMarkerClick, flagFlashMarker, flagUserInitiated) {
        if (DEBUG_REFRESH) {
            console.log(`In onItemSelected from searcher "${lister.getSearcher().searcherName}" flash = ${flagFlashMarker}.`);
        }
        // add it to history (as long as they aren't clicking ON the history tab already); a search item
        // (expanding a saved search, say) is never RE-added to history
        this.addSelectedSearchResultIfAppropriateToHistory(lister, item, false);

        // log it only if it was initiated by user
        if (flagUserInitiated) {
            this.logPlayClickedPoi(item);
        }

        // ATTN: JR 1/31/26 questionable
        // ATTN: This is the end of the chain of calls when a result is selected/highlighted, so now we invoke our helpers for ripple on effects
        // this.displayNeedsRefresh_YouPerformHighlightStatusChange(uid, item, flagFlashMarker);
    }


    /**
     * Handles tab change events in the sidebar.
     * Cancels delayed timers and updates overlay visibility.
     * @param {string} activeTabId - The ID of the newly active tab
     */
    onTabChanges(activeTabId) {
        // update visibility layers which may cause new results to show
        this.cancelDelayedSearchTimer();
        // (the sidebar tabs are app-scoped; with no world loaded there are no searchers or map to refresh)
        if (this.isWorldReady()) {
            this.displayNeedsRefresh_AfterTabChange();
        }

        // scroll play log to end when its tab is selected
        if (activeTabId === 'logplay-tab') {
            this.playLogger.scrollToEnd();
        }
    }




    // ==========================================
    // OpenLayers special markers
    // ==========================================



    /**
     * The highlighted ring marker should always be synchronized with the current EXPANDED item on the CURRENT tab.
     * That means we need to re-evaluate (change/hide) when the user switches tabs, performs a search, collapses or expands an item
     * This function is called from many places, it may be called when we are already highlighting the right thing; that's ok
     * This should be a cheap function, we can rely on the OpenMapManager class to avoid refreshing if its already set
     * ATTN: this is currently being called THREE (3) times on each click (!)
     */
    refreshHighlightedRingMarker(flagFitExtent, flagFlashMarker, debugMessage) {
        // this function will FIGURE OUT the active tab and the expanded (highlighted) item on this tab (or none)
        // and then tell the openMapManager about it
        let uid = null;
        let item = null;

        // First determine the active current searcher (tab)
        const activeSearcher = this.calcActiveSearcherReference();
        if (activeSearcher) {
            // Now determine the expanded (highlighted) item on this searcher's lister (or null)
            const lister = activeSearcher.getLister();
            ({uid,item} = lister.getSelectedResultItemAndUid());
        }

        if (DEBUG_REFRESH) {
            console.log(`ATTN: IN refreshHighlightedRingMarker "${debugMessage}" with uid = "${uid}" activeSearcher=${activeSearcher.searcherName}  fit=${flagFitExtent}  and flash=${flagFlashMarker}.`);
        }

        // ATTN: should we check if item is actual a POI item?

        // Now tell the map to highlight it
        const flagZoomTo = false;
        this.mapManager.highlightPoiByUid(uid, item);
        //
        if (flagFitExtent) {
            this.ensureVisibleMaybeZoomToPoiByUid(uid, item, flagZoomTo, flagFlashMarker);
        }
    }


    /**
     * Shows or hides the region border overlay layer based on whether a region is selected.
     */
    updateRegionOverlayVisibility() {
        // Show region border if a region is selected (region filter is now global via RegionControl)
        const regionOverlayVisible = !!this.selectedRegion;
        this.mapManager.setResultLayerVisibility(MAP_AUX_OVERLAY_LAYERS.REGION, regionOverlayVisible);
    }








    

    // ==========================================
    // AUTO EXTENTS (ZOOM/PAN)
    // ==========================================



    /**
     * Handles an explicit press of the "fit to extent" button.
     * If the user has selected a neighborhood/region via RegionControl, fits the view to the
     * visible markers that lie inside that region; if none do, fits to the region's full extent.
     * With no region selected, fits all currently visible markers.
     */
    autoZoomPanToVisibleFeaturesExplicitlyByRequest() {
        // a selected region restricts the fit to the visible markers inside it
        if (this.selectedRegion) {
            const olGeometry = this.auxDataManager.getRegionGeometryById(this.selectedRegion);
            if (olGeometry) {
                // no visible markers inside the region -> fit the region itself
                const extent = this.mapManager.computeVisibleFeaturesExtentWithinGeometry(olGeometry) || olGeometry.getExtent();
                this.mapManager.fitToExtentWithStyle(extent, AUTO_ZOOM_STYLE.ZOOM_IN, null);
                return;
            }
            // no geometry available for the region -> fall through to visible-features behavior
        }
        // zoom and pan to exactly cover the visible features
        this.mapManager.autoZoomAllVisibleFeatureExtents(AUTO_ZOOM_STYLE.ZOOM_IN);
    }

    /**
     * HELPER: Auto-zooms the map to fit all visible feature extents.
     * @param {string} autoZoomStyle - see constants.js
     */
    autoZoomAllVisibleFeatureExtents(autoZoomStyle) {
        this.mapManager.autoZoomAllVisibleFeatureExtents(autoZoomStyle);
    }

    autoZoomPanToResultSet(results) {
        this.mapManager.autoZoomPanToResultSet(results, AUTO_ZOOM_STYLE.ZOOM_IN);
    }

    /**
     * Hook for potential auto-zoom/pan after overlay visibility changes.
     * Currently a no-op; could be enabled to ensure all visible items are within extent.
     */
    maybeZoomPanOnUpdatedOverlayVisibility() {
        // we COULD try to ensure all visible items are within extent, but probably not
        // this.autoZoomAllVisibleFeatureExtents(AUTO_ZOOM_STYLE.EXPAND);
    }




    /**
     * Make sure a poi location is in view, and optionally zoom in to it
     * @param {string} uid - Unique identifier of the POI
     * @param {Object} item - The POI item with geometry data
     * @param {boolean} flagZoomTo - True to zoom to the POI, false to just make sure it is in extent
     */
    ensureVisibleMaybeZoomToPoiByUid(uid, item, flagZoomTo, flagFlashMarker) {
        // flagZoomTo will be true if user EXPLICITLY asks to go to zoom to the item, false if they are just selecting it
        // make sure it is visible in extent (with flag governing whether to zoom to it)
        if (flagZoomTo) {
            flagFlashMarker = true;
        }
        return this.mapManager.zoomExtentToPoiUid(uid, item, flagZoomTo ? AUTO_ZOOM_STYLE.ZOOM_IN : AUTO_ZOOM_STYLE.PAN_EXPAND, flagFlashMarker);
    }



    /**
     * Pans and/or zooms the map to ensure the selected region is visible.
     * Looks up the region geometry and fits the map extent to it.
     * @param {string} regionId - The region ID to make visible
     */
    ensureVisibilityOfRegionByIdMaybe(regionId) {
        // user selects a new region, we might jump to its extent
        const flagZoomTo = false;

        const olGeometry = this.auxDataManager.getRegionGeometryById(regionId);
        if (!olGeometry) {
            return;
        }

        const extent = olGeometry.getExtent();
        this.mapManager.fitToExtentWithStyle(extent, flagZoomTo ? AUTO_ZOOM_STYLE.ZOOM_IN : AUTO_ZOOM_STYLE.PAN_EXPAND, null);
    }









    // ================================
    // ANCHOR HELPERS
    // ================================
    /**
     * Calculates the distance from an item to the current anchor as a formatted HTML string.
     * Returns an empty string if no anchor is set, or "Current anchor" if the item is the anchor itself.
     * @param {Object} item - The POI item to measure distance from
     * @returns {string} HTML string with formatted distance (e.g., "0.3 mi from anchor"), or empty string
     */
    calcDistanceToAnchorAsHtmlString(item) {
        const anchorItem = this.currentAnchor.item;
        if (!anchorItem) {
            return '';
        }
        if (item.uid == anchorItem.uid) {
            return '<span class="currentAnchor">Current anchor</span>';
        }
        //
        const targetUnit = 'imperial';
        const flagVerbose = true;
        //
        const distInNativeUnits = distanceBetweenTwoItems(item, anchorItem);
        if (distInNativeUnits === null) {
            // one of the two has no point: no distance to show (null printed as "0 feet")
            return '';
        }
        const rasterScale = this.mapManager.isRasterMode() ? (this.getRasterConfigValue("metersPerPixel", -1)) : false;
        const anchorString = convertDistanceGeometryUnitsToNiceString(distInNativeUnits, targetUnit, flagVerbose, rasterScale);
        return anchorString + ' from anchor';
    }













    // ================================
    // CUSTOMIZED ITEM HELPERS FOR BOOKMARKS ETC.
    // ================================

    // ATTN: there 


    /**
     * Saves edited bookmark fields from a values dictionary into a POI item and persists changes.
     * Copies each field from valuesDict into the item, updates customization state,
     * ensures the item is on the appropriate internal searcher, and saves to storage.
     * @param {Object} item - The POI item to update
     * @param {Object} valuesDict - Dictionary of field values to save (from BookmarkDialog)
     * @param {Lister} listerp - The Lister instance where the edit was triggered
     */
    doSaveEditedItem(item, valuesDict, listerp) {
        // Copy values from valuesDict to item for each field in fieldsToSave
        // set values
        for (const field of CUSTOM_ITEM_FIELDS_BOOKMARK) {
            if (field in valuesDict) {
                if (valuesDict[field]) {
                    item[field] = valuesDict[field];
                } else {
                    // if its not set, or set false, that means delete
                    delete item[field];
                }
            }
        }

        // now record whether it is customized
        this.updateItemIsCustomized(item);

        // what searcher was this edit triggered on?
        const searcher = listerp.getSearcher();

        // NOW, we newly allow this to be triggered on OTHER tabs besides bookmarks tab, in which case we need to ADD it to bookmarks tab
        const saveInternalSearcher = (item.ptype=='search') ? this.savedSearcher : this.bookmarkSearcher;
        if (saveInternalSearcher != searcher) {
            // we triggered it elsewhere so we need to add it to this internal searcher
            // is it already ON that internal searcher
            const existingItem = saveInternalSearcher.getListerResultById(item.uid);
            if (!existingItem) {
                saveInternalSearcher.addItemRefDirect(item, 'editing from dif tab', false);
            }
        }

        // save to storage and refresh the other tab if needed
        if (saveInternalSearcher != searcher) {
            // save bookmarks, etc.
            saveInternalSearcher.saveResultsToStorage();
        } else {
            searcher.saveResultsToStorage();
        }

        // refresh current list
        this.displayNeedsRefresh_AfterItemEdited(item, searcher);
    }






    /**
     * Recalculates and sets or clears the isCustomized flag on an item.
     * Checks all bookmark custom fields; if any are set (and not in the ignore list),
     * marks the item as customized. Otherwise, deletes the isCustomized property.
     * @param {Object} item - The POI item to evaluate
     */
    updateItemIsCustomized(item) {
        // we try to build some fields to ignore when computing isCustomized
        const ignoreFields =  !item[FIELD_ENABLE_CUSTOM_APPEARANCE] ? FIELDS_CUSTOMIZE_IGNORE_ON_DISABLED_CUSTOM_APPEARANCE : [];

        for (const field of CUSTOM_ITEM_FIELDS_BOOKMARK) {
            if (item[field] !== undefined) {
                if (!ignoreFields.includes(field)) {
                    item.isCustomized = true;
                    return;
                }
            }
        }
        // not customized so clear this field
        delete item.isCustomized;
    }


    /**
     * Creates a minimal stub copy of a POI item for serialization to localStorage.
     * The stub contains only the uid, ptype (set to POISTUB), and any custom fields.
     * Non-POI items (e.g., searches) are returned unchanged.
     * @param {Object} item - The full POI item to create a stub from
     * @param {Array<string>} fields - List of custom field names to include in the stub
     * @returns {Object} A minimal stub for serialization, or the original item if not a POI
     */
    createItemCustomStubForSerializing(item, fields) {
        if (!PTYPES_POIDB.includes(item.ptype)) {
            // if its not a poi stub, then we leave it alone
            return item;
        }

        // we want to make a copy, with ONLY the uid and custom fields
        const itemStub = {
            uid: item.uid,
            ptype: PTYPES.POISTUB,
        }
        for (const field of fields) {
            if (item[field] !== undefined) {
                itemStub[field] = item[field];
            }
        }
        return itemStub;
    }



    /**
     * Finds the real POI item from a loaded serialized stub and merges custom user fields into it.
     * Looks up the POI by uid in the data manager, copies custom fields from the stub,
     * and returns the real item. If the POI is not found, returns the stub with POISTUB ptype.
     * @param {Object} itemStub - A serialized stub with uid and custom fields
     * @returns {Object|null} The real POI item with merged custom fields, the stub if not found, or null if input is null
     */
    locateAndModifyPoiFromLoadedUserStub(itemStub, fields, flagMakeCopy) {
        // we have loaded a serialized itemStub, now we wil find the poi item, copy custom user fields into it, mark it as customized, and return the real poi item
        // if not found, just return the stub with a ptype set to stub
        if (!itemStub) {
            return null;
        }

        if (itemStub.ptype != PTYPES.POISTUB) {
            // if its not a poi stub, then we leave it alone
            return itemStub;
        }

        const uid = itemStub.uid;
        // find the poi data item by uid

        // LETS STRESS TEST AND PRETEND IT COULDNT BE FOUND
        let item = this.poiDataManager.findItemByUid(uid);


        if (!item) {
            // not found
            itemStub.ptype = PTYPES.POISTUB; // this should already be set
            return itemStub;
        }

        if (flagMakeCopy) {
            // make a copy of the item dont modify real one
            item = deepCopy(item);
        }

        // note this will update the isCustomized field
        this.mergeUserFeaturesFromStubIntoItem(itemStub, item, fields);

        // and then return the real poi item; the stub can be discarded at this point
        return item;
    }


    /**
     * Copies custom user fields from a serialized stub into the real POI item.
     * After copying, recalculates the isCustomized flag on the item.
     * @param {Object} itemStub - The serialized stub containing custom field values
     * @param {Object} item - The real POI item to merge fields into
     * @param {Array<string>} fields - List of custom field names to copy
     */
    mergeUserFeaturesFromStubIntoItem(itemStub, item, fields) {
        // ok we found the item, now we're going to move the custom user values over INTO it
        for (const field of fields) {
            if (itemStub[field] !== undefined) {
                // copy over the field from stub to real item
                item[field] = itemStub[field];
            }
        }
        this.updateItemIsCustomized(item);
    }


    /**
     * Clears the specified custom fields from an item and recalculates isCustomized.
     * @param {Object} item - The POI item to clear fields from
     * @param {Array<string>} customFields - List of custom field names to delete
     */
    clearItemCustomFields(item, customFields) {
        // clear some custom fields from a db item
        for (const field of customFields) {
            if (field in item) {
                delete item[field];
            }
        }
        this.updateItemIsCustomized(item);
    }












    /**
     * Calculates the icon image path for a POI item.
     * Delegates to ImageHelper for icon lookup.
     * @param {Object} item - The POI item to calculate the icon for
     * @param {Searcher} searcher - The searcher requesting the icon (unused, reserved for future use)
     * @returns {string} Resolved path to the icon image
     */
    calcIconPathForItem(item) {
        return this.imageHelper.calcIconPathForItem(item);
    }



    /**
     * Calculates the icon image path for relative path
     * Delegates to ImageHelper for icon lookup.
     * @param {Object} item - The POI item to calculate the icon for
     * @param {Searcher} searcher - The searcher requesting the icon (unused, reserved for future use)
     * @returns {string} Resolved path to the icon image
     */
    calcIconPathForRelativeImagePath(relativePath) {
        return this.imageHelper.calcIconPathForRelativeImagePath(relativePath);
    }


























































    // ================================
    // CENTRALIZED DISPLAY UPDATE FUNCTIONS
    // THESE ARE CALLED WHEN SOME ITEM OR DETAIL HAS CHANGED THAT MAY REQUIRE A MORE COMPLICATED REFRESH THAT SHOULD BE DONE EFFICIENTLY
    //
    // Some kinds of things these functions may do is call:
    //  Lister.updateAppearanceOfItemByUid()
    //  reperformAllResultListSearchesAfterBatchImportUpdate()
    //  refreshRegionVisualization()
    //  updateResultLayersVisibility()
    //  updatePartialAppearanceOfItemByUidOnAllSearchers();
    //  rePerformSearchToFullyRefreshResultDisplay(false);
    //  refreshHighlightedRingMarker();
    // updateOverlayLayersVisibility
    //
    // Now lets consider what kinds of changes might have ripple effects that require different levels of updates
    //  1. When an item is "selected" in a result (aka highlighted, aka expanded), we update a highlight layer visual that gets shown on the map
    //     When an item is clicked on on the map, we also trigger a select/highlight action in same way.
    //     When an item is collapsed/unselected/unhighlighted we need to clear the selected highlight ring layer on map.
    //     Neither of these cause OTHER knock-on update effects.
    //  2. Whenever we change tabs, we may need to newly expand/highlight the currently highlighted item on the new tab, which may require scrolling to it
    //     Or if that item is NOT on the newly switched to tab, we may need to collapse whatever item IS currently open/expanded/selected/highlighted and DISABLE highlight ring layer
    //  3. Whenever we toggle a bookmarked item on or off, or delete a bookmark (or history item), we will need to
    //     1) Rebuild that result lister (bookmarks or history); small so should be fast
    //     2) Rebuild that map layer (bookmarks or history); small so should be fast
    //  4. Whenever we EDIT a bookmark, we need to do same as toggling one, but ALSO it may be that the feature on the main POI DB search layers (white, yellow, reverse, advanced)
    //     may need visual updating.  For max efficiency this could be handled with a call to refreshTheFeatureOnAllLayersByUid() which will just say to
    //     delete and recreate (if appropriate) the feature corresponding to the changed (edited, deleted, eadded) uid.
    //  5. When system visual options change we could try to figure out if a major change is needed; if so, it would be appropriate to re-run everything since it is rate
    //  6. There are a bunch of things that COULD effect advanced search results, including region select, bookmark changes, etc.  
    //     It may not be essential to force a re-running of advanced search on all of these, but maybe on region select; otherwise user can re-run
    //
    //  The philosophy of the functions below are to perform the ripple knock-on effects, after the caller has done the main effect.
    // Philosophy part 2 is that all searcher(lister) refreshing should be one through one of these; so look for functions that call searcherRefresh and move them to here
    // ================================



    /**
     * Triggered after an item's highlight (expand/collapse) status changes.
     * Currently a no-op placeholder for potential future highlight ring refresh logic.
     * @param {string|null} uid - UID of the highlighted item, or null if collapsed
     * @param {Object|null} item - The highlighted item, or null if collapsed
     * @param {boolean} flagFlashMarker - Whether to flash the marker on the map
     */
    displayNeedsRefresh_AfterHighlightStatusChange(uid, item, flagFlashMarker) {
        if (DEBUG_REFRESH) {
            //console.log(`In AFTER displayNeedsRefresh_AfterHighlightStatusChange uid=${uid}; dName=${item ? item.dName : 'null'}.`);
        }
        //this.refreshHighlightedRingMarker(true, flagFlashMarker, 'displayNeedsRefresh_AfterHighlightStatusChange');
    }

    /**
     * Performs a highlight ring marker refresh when a highlight status change needs to be applied.
     * Unlike AfterHighlightStatusChange, this method actively refreshes the ring marker.
     * @param {string|null} uid - UID of the highlighted item, or null to clear
     * @param {Object|null} item - The highlighted item, or null to clear
     * @param {boolean} flagFlashMarker - Whether to flash the marker on the map
     */
    displayNeedsRefresh_YouPerformHighlightStatusChange(uid, item, flagFlashMarker) {
        if (DEBUG_REFRESH) {
            console.log(`In displayNeedsRefresh_YouPerformHighlightStatusChange uid=${uid}; dName=${item ? item.dName : 'null'}.`);
        }
        this.refreshHighlightedRingMarker(true, flagFlashMarker, 'displayNeedsRefresh_YouPerformHighlightStatusChange');
    }


    /**
     * Triggered after the user switches sidebar tabs.
     * Updates overlay and region visibility, performs deferred searcher refresh if needed,
     * refreshes the highlight ring marker, and re-selects the current result on the new tab.
     */
    displayNeedsRefresh_AfterTabChange() {
        if (DEBUG_REFRESH) {
            console.log(`In displayNeedsRefresh_AfterTabChange 1.`);
        }

        const activeSearcher = this.calcActiveSearcherReference();

        this.updateOverlayLayersVisibility();
        this.updateRegionOverlayVisibility();

        if (DEBUG_REFRESH) {
            console.log(`In displayNeedsRefresh_AfterTabChange 2.`);
        }

        // deferred searcher refresh of current tab?
        if (activeSearcher) {
            const isRefreshing = activeSearcher.getNeedsRefresh();
            this.refreshSearcherNowIfNeeded(activeSearcher);

            // IF we aren't doing a full refresh then we do this
            if (!isRefreshing) {
                // refresh ring marker
                this.refreshHighlightedRingMarker(false, false, 'displayNeedsRefresh_AfterTabChange');
                // jump to highlighted item if there was one
                activeSearcher.getLister().reSelectCurrentResultOnLister();
            }
        }




    
        
        if (DEBUG_REFRESH) {
            console.log(`In displayNeedsRefresh_AfterTabChange 4.`);
        }
    }


    /**
     * Triggered after a bookmark is added or removed.
     * If the change could affect item appearance (major change), forces full refresh on affected searchers.
     * Otherwise, does a partial appearance update and deferred refresh on the bookmark searcher.
     * @param {string} uid - UID of the bookmarked/unbookmarked item
     * @param {boolean} flagPossibleMajorChange - True if customization state changed (may affect rendering)
     */
    displayNeedsRefresh_AfterBookmarkStatusChanged(uid, flagPossibleMajorChange) {
        if (DEBUG_REFRESH) {
            console.log(`In displayNeedsRefresh_AfterBookmarkStatusChanged.`);
        }
        // all searchers may need to update if only to show actionbar button state
        if (flagPossibleMajorChange) {
            this.forceAllSearchersWithThisUidToNeedRefresh(uid, this.bookmarkSearcher);
        } else {
            this.updatePartialAppearanceOfItemByUidOnAllSearchers(uid);
            this.searcherNeedsRefresh(this.bookmarkSearcher);
            // change style for uid
            this.mapManager.refreshAllFeaturesWithUid(uid);
            // we may need to FORCE a full bookmark resfresh if we overlay it
            this.forceRrefreshBookmarksIfNotActiveButVisible();
        }
    }


    forceRrefreshBookmarksIfNotActiveButVisible() {
        if (!this.bookmarkSearcher.getNeedsRefresh()) {
            return;
        }
        if (!this.isPersistentlyOverlaidLayer(this.bookmarkSearcher)) {
            return;
        }
        this.refreshSearcherNowIfNeeded(this.bookmarkSearcher);
    }

    isPersistentlyOverlaidLayer(searcher) {
        // return true if this searcher's layer is visible on the map even though it's not the active tab
        const activeSearcherName = this.calcActiveSearcherName();
        if (searcher.getName() === activeSearcherName) {
            return false;
        }
        const overlayState = this.mapManager.controls.overlayControl
            ? this.mapManager.controls.overlayControl.getVisibilityState()
            : {};
        return overlayState[searcher.getName()] ?? false;
    }


    displayNeedsRefresh_AfterOverlayLayerChange() {
        this.forceRrefreshBookmarksIfNotActiveButVisible();
    }


    /**
     * Triggered after a saved search is added or removed.
     * Updates actionbar icon state on all searchers and defers refresh on the saved searcher.
     * @param {string} uid - UID of the saved/unsaved search item
     */
    displayNeedsRefresh_AfterSavedSearchStatusChanged(uid) {
        if (DEBUG_REFRESH) {
            console.log(`In displayNeedsRefresh_AfterSavedSearchStatusChanged searcher=${this.savedSearcher}.`);
        }
        // all searchers may need to update if only to show actionbar button state
        this.updatePartialAppearanceOfItemByUidOnAllSearchers(uid);
        this.searcherNeedsRefresh(this.savedSearcher);
    }


    /**
     * Triggered after an item's custom properties are edited via BookmarkDialog.
     * Forces a full refresh on all searchers that contain this item's uid.
     * @param {Object} item - The edited item
     * @param {Searcher} searcher - The searcher where the edit was triggered
     */
    displayNeedsRefresh_AfterItemEdited(item, searcher) {
        if (DEBUG_REFRESH) {
            console.log(`In displayNeedsRefresh_AfterItemEdited (${searcher.searcherName}).`);
        }
        const uid = item? item.uid : null;

        // all searchers may need to update and this MIGHT require more than an actionbar update (custom userlabel may have changed, etc.)
        // new -- force all searchers with this uid to refresh, but no matter what the searcher just operated even if it doesnt own the uid anymore
        this.forceAllSearchersWithThisUidToNeedRefresh(uid, searcher);
    }

    /**
     * Triggered after the anchor POI changes (set or cleared).
     * Re-displays all result lists (anchor distance may appear/change), updates actionbar icons,
     * and ensures the anchor is visible on the map.
     * @param {string|null} uid - UID of the newly anchored item, or null if cleared
     * @param {Object|null} item - The anchored item, or null if cleared
     */
    displayNeedsRefresh_AfterAnchorChanged(uid, item) {
        if (DEBUG_REFRESH) {
            console.log(`In displayNeedsRefresh_AfterAnchorChanged.`);
        }

        // ATTN:  BECAUSE ANCHOR DISTANCE IS SHOWN ON ALL TABS, WE NEED TO REFRESH ALL RESULT LISTS TABS
        this.reDisplayAllResultListsAfterMajorChange(false);

        // all searchers may need to update if only to show actionbar button state
        this.updatePartialAppearanceOfItemByUidOnAllSearchers(uid);
        //
        if (this.isAnchoredByUid(uid)) {
            // fit it!
            const flagZoomTo = false;
            this.ensureVisibleMaybeZoomToPoiByUid(uid, item, flagZoomTo, false);
        }
    }

    /**
     * Triggered after visual settings change (e.g., theme, marker style, dim-outside-region).
     * Re-runs all searches, refreshes region visualization, and updates layer opacity.
     */
    displayNeedsRefresh_AfterVisualOptionsChanged() {
        if (DEBUG_REFRESH) {
            console.log(`In displayNeedsRefresh_AfterVisualOptionsChanged.`);
        }

        // re-run all searches to play it safe
        this.reperformAllSearchesAfterMajorChange();
        // refresh region visualization (may change due to dim-outside-region setting)
        this.refreshRegionVisualization();
        // Update inactive layer opacity on all result layers
        this.mapManager.updateResultLayersVisibility();
    }


    /**
     * Triggered after the region selection changes.
     * Currently a placeholder; region-related refreshes are handled by the caller (doRegionSelectUpdate).
     */
    displayNeedsRefresh_AfterRegionSelect() {
            if (DEBUG_REFRESH) {
            console.log(`In displayNeedsRefresh_AfterRegionSelect.`);
        }
        // is there anything we need to do here or does caller do it all?
    }


    /**
     * Triggered after map features are rebuilt on a result layer (e.g., after a new search).
     * Refreshes the highlight ring marker and updates anchor POI visibility.
     * @param {string} layerName - Name of the layer whose features changed
     */
    displayNeedsRefresh_AfterLayerFeaturesChange(layerName) {
        if (DEBUG_REFRESH) {
            console.log(`In displayNeedsRefresh_AfterLayerFeaturesChange for layer ${layerName}.`);
        }

        // we have created featured on the map after a new search, so now we may need to highlight the selected item and anchor
        // update highlighted ring marker
        this.refreshHighlightedRingMarker(false, false, 'displayNeedsRefresh_AfterLayerFeaturesChange');
        // update anchor POI visibility (anchor may now be shown on this layer)
        this.mapManager.updateAnchorPoiVisibility();
    }


    /**
     * Triggered after an item is deleted from a searcher's results.
     * If the deletion could affect other searchers' rendering (major change), forces full refresh
     * on all searchers with this uid. Otherwise, does partial appearance update and deferred refresh.
     * @param {string} uid - UID of the deleted item
     * @param {Object} item - The deleted item
     * @param {Searcher} searcher - The searcher the item was deleted from
     * @param {boolean} flagPossibleMajorChange - True if customization state changed (e.g., bookmark deletion)
     */
    displayNeedsRefresh_AfterItemDelete(uid, item, searcher, flagPossibleMajorChange) {
        if (DEBUG_REFRESH) {
            console.log(`In displayNeedsRefresh_AfterItemDelete.`);
        }

        // ATTN: do we need to be so agressive regarding rePerform??

        if (flagPossibleMajorChange) {
            this.forceAllSearchersWithThisUidToNeedRefresh(uid, searcher);
        } else {
            // deleting from bookmark could mean it loses action bar button state
            this.updatePartialAppearanceOfItemByUidOnAllSearchers(uid);
            // and full refresh of current searcher
            this.searcherNeedsRefresh(searcher);
        }
    }


    /**
     * Triggered after an item is added to the history searcher.
     * Defers a refresh on the history searcher tab.
     * @param {Object} item - The item that was added to history
     */
    displayNeedsRefresh_AfterResultItemAddHistory(item) {
        if (DEBUG_REFRESH) {
            console.log(`In displayNeedsRefresh_AfterResultItemAddOrDelete.`);
        }
        // and full refresh of saved searcher
        this.searcherNeedsRefresh(this.historySearcher);
        // deleting from bookmark could mean it loses action bar button state?? NO
        // this.updatePartialAppearanceOfItemByUidOnAllSearchers(uid);
    }





    /**
     * Triggered after a bulk data import into a searcher.
     * Immediately re-performs the search to refresh the result display.
     * @param {Searcher} searcher - The searcher that received the imported data
     */
    displayNeedsRefresh_AfterBulkImport(searcher) {
        if (DEBUG_REFRESH) {
            console.log(`In displayNeedsRefresh_AfterBulkImport.`);
        }
        searcher.rePerformSearchToFullyRefreshResultDisplay(false);
    }

    /**
     * Triggered after a searcher's search results are cleared.
     * Clears the highlight ring marker since the selected item no longer exists.
     * @param {Searcher} searcher - The searcher whose results were cleared
     */
    displayNeedsRefresh_AfterSearchCleared(searcher) {
        // remove highlight ring if appropriate
        this.displayNeedsRefresh_YouPerformHighlightStatusChange(null, null, false);
    }




    displayNeedsRefresh_AfterCustomEditItem(uid) {
        // this happens after user EDITS custom settings of an item; in this case we ALREADY had each search tab update THAT item
        // but we need BOOKMARKS to completely refresh now if they are not yet showing
        const activeSearcher = this.calcActiveSearcherReference();
        if (activeSearcher != this.bookmarkSearcher) {
            this.bookmarkSearcher.reBuildReDisplayResultList();
        }
    }


    /**
     * Creates a custom user bookmark at the given map coordinates and adds it to the bookmark searcher.
     * @param {number} x - X coordinate (EPSG:3857)
     * @param {number} y - Y coordinate (EPSG:3857)
     */
    createCustomUserBookmarkAtLocation(x, y) {
        const uid = `cbm_user_${Date.now()}`;
        const item = {
            uid: uid,
            ptype: PTYPES.CUSTOM,
            pcat: 'custom',
            pcatg: 'custom',
            dName: MAP_CONSTANTS.USER_BOOKMARK_NAME,
            address: `${Math.round(x)},${Math.round(y)}`,
            lead: 'user',
            loclabel: '??',
            jregion: this.lookupJregionFromCoordinates(x, y),
            geometry: {
                type: 'Point',
                coordinates: [x, y],
            },
        };
        this.bookmarkSearcher.addOrRemoveItemByUidItem(uid, item, 'creating custom user bookmark');
        this.displayNeedsRefresh_AfterCustomEditItem(uid);
        this.searcherNeedsRefresh(this.bookmarkSearcher);
        // switch to bookmark tab
        this.switchToTabBySearcherName(this.bookmarkSearcher.searcherName);
        // select it?
        // wait a frame for lister to update
        requestAnimationFrame(() => {
            this.someoneElseInstructsGoToAndSelectResultItemByUid(uid, true);
        });
    }


    /**
     * Looks up the jregion identifier for a given map coordinate by testing against region polygon geometries.
     * @param {number} x - X coordinate (EPSG:3857)
     * @param {number} y - Y coordinate (EPSG:3857)
     * @returns {string} The jregion identifier, or empty string if no region contains the point
     */
    lookupJregionFromCoordinates(x, y) {
        const regionData = this.auxDataManager.getRegionData();
        if (!regionData) {
            return '';
        }
        for (const region of regionData) {
            if (region.geometry && isPointInPolygonGeometry(x, y, region.geometry)) {
                return region.jregion || '';
            }
        }
        return '';
    }


    // ==========================================
    // HELPER FUNCTIONS FOR DISPLAY UPDATES
    // ==========================================

    /**
     * Updates a map marker layer with new result features.
     * Pass-through to the map manager. Clears existing features and replaces with new results.
     * @param {string} layerName - Name of the layer to update
     * @param {Array<Object>} results - Array of result items to display as markers
     */
    appUpdateMapFeatureMarkersLayerWithNewResults(layerName, results) {
        if (DEBUG_REFRESH) {
            console.log(`  cbm: Full rebuild of map manager feature markers for layer ${layerName}.`)
        }
        //
        this.mapManager.recreateAllMapFeatureMarkersForLayerWithNewResults(layerName, results);
        // now knock on effects
        this.displayNeedsRefresh_AfterLayerFeaturesChange(layerName);
    }




    /**
     * Re-runs searches on all registered searchers to fully refresh result displays.
     * Called after major changes (e.g., visual options) that may affect all search results.
     */
    reperformAllSearchesAfterMajorChange() {
        // ATTN: TODO - in future we may want to do these in order, from bookmarks first, since that COULD effect rendering of others
        this.forEachSearcher(searcher => searcher.rePerformSearchToFullyRefreshResultDisplay(false));
    }

    /**
     * Re-displays all result lists across all searchers after a major change.
     * If flagForceImmediate is true, rebuilds immediately; otherwise defers via searcherNeedsRefresh.
     * @param {boolean} flagForceImmediate - True to rebuild all lists immediately, false to defer
     */
    reDisplayAllResultListsAfterMajorChange(flagForceImmediate) {
        // ATTN: TODO - in future we may want to do these in order, from bookmarks first, since that COULD effect rendering of others
        this.forEachSearcher(searcher => {
            if (flagForceImmediate) {
                searcher.reBuildReDisplayResultList()
            } else {
                // deferred refresh
                this.searcherNeedsRefresh(searcher);
            }
        });
    }



    /**
     * Updates the partial appearance (e.g., actionbar icon state) of an item across all searchers.
     * Used when an item's bookmark, saved, or anchor status changes and all listers showing
     * that item need their rendered appearance updated without a full re-search.
     * @param {string} uid - Unique identifier of the item to update
     */
    updatePartialAppearanceOfItemByUidOnAllSearchers(uid) {
        this.forEachSearcher(searcher => { searcher.updatePartialAppearanceOfItemByUid(uid); });
    }

    /**
     * Forces a full refresh on all searchers that contain the given uid in their results,
     * plus the specified orSearcher unconditionally. Uses deferred refresh via searcherNeedsRefresh.
     * @param {string} uid - Unique identifier of the item that changed
     * @param {Searcher} orSearcher - A searcher to always refresh regardless of whether it has the uid
     */
    forceAllSearchersWithThisUidToNeedRefresh(uid, orSearcher) {
        this.forEachSearcher(searcher => { if (searcher.getListerResultById(uid) || searcher==orSearcher) this.searcherNeedsRefresh(searcher); });
    }


    /**
     * Updates visibility of all overlay layers on the map.
     * Called when user changes map overlay settings or switches tabs.
     */
    updateOverlayLayersVisibility() {
        if (!this.isInitialized()) {
            return null;
        }
        //
        this.mapManager.updateResultLayersVisibility();
        // Update anchor POI visibility based on whether it's shown elsewhere
        this.mapManager.updateAnchorPoiVisibility();
        // force extent fit?
        this.maybeZoomPanOnUpdatedOverlayVisibility();
    }



    /**
     * Marks a searcher as needing refresh. If the searcher is currently visible (active tab),
     * performs the refresh immediately. Otherwise, defers it until the tab is switched to.
     * @param {Searcher} searcher - The searcher to mark for refresh
     */
    searcherNeedsRefresh(searcher) {
        const activeSearcher = this.calcActiveSearcherReference();
        if (activeSearcher == searcher) {
            // refresh now since it's visible
            searcher.rePerformSearchToFullyRefreshResultDisplay(false);
        } else if (searcher) {
            // flagged even when NO searcher is active (the sidebar's Log tab has none): it used to
            // need an active one, so lists changed while the Log tab was open never refreshed
            searcher.setNeedsRefresh(true);
        }
    }

    /**
     * Performs a deferred refresh on a searcher if it was previously flagged as needing one.
     * Clears the needsRefresh flag and re-performs the search to fully refresh the result display.
     * @param {Searcher} searcher - The searcher to conditionally refresh
     */
    refreshSearcherNowIfNeeded(searcher) {
        if (searcher.getNeedsRefresh()) {
            searcher.rePerformSearchToFullyRefreshResultDisplay(false);
        }
    }

















    initializeMainPanelTabs() {
        // this.setMainPanelTabsVisible(true);
        // ATTN: test
        // this.loadPdfInReadingPane("/media/ThePastIsACrookedGame_twocol_letter.pdf", "LEADS");
        //this.getPdfViewer().expandBookmarkByLabel("LEADS");
    }












    async goToLead(item, leadId) {
        const pdfViewer = this.getPdfViewer();
        //
        const stayOnMapAfterReadButtonPress = this.stateManager.getStayOnMapAfterReadButtonPress();
        if (!stayOnMapAfterReadButtonPress) {
            this.ensurePdfViewerVisible();
        }
        //
        // skip last-page restore: we're loading specifically to jump to this lead
        await this.loadLastPdfIfNothingLoaded(false, true);
        const found = await pdfViewer.goToBookmarkByLeadIdOrLabel(leadId, true);
        if (found) {
            // highlight it? switch to tab?
            if (!this.isMainPanelReadingTabActive()) {
                // TODO: Check if the setting stayOnMapAfterReadButtonPress  is set, if so just display info about what page the lead is on, and display it with a warning about stayOnMapAfterReadButtonPress setting
                // otherwise, switch to pdf tab

                if (stayOnMapAfterReadButtonPress) {
                    const pageNumber = pdfViewer.getCurrentPageNumber();
                    // the one toast built as markup (the big bold numbers): its data is escaped
                    let messageHtml = `Lead <span class="jrBigBoldNumber">${escapeHtml(leadId)}</span> is on page <span class="jrBigBoldNumber">${escapeHtml(pageNumber)}</span> of the pdf.`
                    if (this.warnOnFirstStayOnMapAfterRead !== false) {
                        this.warnOnFirstStayOnMapAfterRead = false;
                        messageHtml += "\n\n[Auto switch to pdf is disabled]"
                    }
                    showStatusHelpful(messageHtml, MAPAPP_DEFAULTS.STATUS_TOAST_LONG_DURATION_MS, { trustedHtml: true });
                } else {
                    // Switch to tab, force immediate resize (bypasses 500ms debounce),
                    // then re-navigate so highlight lands at correct position
                    await this.switchToMainPanelTabByIdAsync(ID_MAIN_READING_TAB);
                    this.ensurePdfViewerInitialized();
                    await pdfViewer.adjustToViewerSizeChanges();
                }
                // should we always go to bookmark just in case
                await pdfViewer.goToBookmarkByLeadIdOrLabel(leadId, true);
            }
            // test
            // pdfViewer.expandBookmarkByLabel('LEADS');
            this.logPlayReadLead(item, leadId, true);
            // the reader's keys work at once, without first clicking the page: the focus was left
            // on the sidebar button that asked for the lead
            if (this.isMainPanelReadingTabActive()) {
                pdfViewer.focusViewer();
            }
        } else {
            if (pdfViewer.hasPdf()) {
                // log read attempt
                this.logPlayReadLead(item, leadId, false);
                // error message (plain text: the lead can come from an imported item or a pdf outline title)
                const message = `Lead "${leadId}" not found in PDF.`;
                // show warning toast on screen
                if (this.isMainPanelMapTabActive()) {
                    showStatusWarning(message);
                } else {
                    // show toast warning in pdf viewer
                    pdfViewer.showWarningToast(message);
                }
            } else {
                const message = `LOAD PDF FIRST!`;
                if (this.isMainPanelMapTabActive()) {
                    showStatusWarning(message);
                } else {
                    pdfViewer.showWarningToast(message);
                }
            }
        }
        return found;
    }


    ensureMapTabVisible() {
        // first make sure sidebar is not too wide
        this.ensureMainPanelVisible();
        //
        if (!this.isMainPanelMapTabActive()) {
            this.switchToMainPanelMapTab();
        }
    }


    ensurePdfViewerVisible() {
        // The bar can now be visible without the Case Book tab (e.g. only Background Music open),
        // so key off the Case Book tab's own visibility rather than the whole bar.
        if (!this.getMainPanelTabsVisible() || !this.isMainPanelTabVisible(ID_MAIN_READING_TAB)) {
            this.setMainPanelTabsVisible(true);
            this.switchToMainPanelReadingTab();
        }
    }

    togglePdfViewer() {
        if (this.getMainPanelTabsVisible() && this.isMainPanelTabVisible(ID_MAIN_READING_TAB)) {
            this.closeReadingTab();
        } else {
            this.setMainPanelTabsVisible(true);
            this.switchToMainPanelReadingTab();
        }
    }


    /**
     * Toggles the PDF reading-pane tab on/off. Wired to the global Ctrl+Q hotkey.
     */
    toggleMapPdfPanel() {
        this.togglePdfViewer();
    }


    // ==========================================
    // GLOBAL HOTKEYS
    // ==========================================

    /**
     * Installs document-level hotkeys that fire regardless of focus
     * (PDF viewer, search bars, map, etc.).
     */
    setupGlobalHotkeys() {
        // document-level: bind once per page (see constructor), or after a world switch each hotkey
        // would fire once per world loaded so far (an even count makes Ctrl+Q toggle twice = nothing)
        if (this.globalHotkeysInstalled) return;
        this.globalHotkeysInstalled = true;

        document.addEventListener('keydown', (e) => {
            // Ctrl+Q (or Cmd+Q): toggle the PDF reader tab
            if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey &&
                (e.key === 'q' || e.key === 'Q')) {
                e.preventDefault();
                this.toggleMapPdfPanel();
            }
            // Ctrl+G (or Cmd+G): open the "go to lead" dialog, regardless of
            // whether focus is on the map, a search tab, or the PDF reader.
            if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey &&
                (e.key === 'g' || e.key === 'G')) {
                e.preventDefault();
                this.showPdfLeadGoToDialogFromAnywhere();
            }
            // Ctrl+L (or Cmd+L): open the highlights dialog and immediately
            // start a new freeform note, regardless of current focus.
            if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey &&
                (e.key === 'l' || e.key === 'L')) {
                e.preventDefault();
                this.showPdfAddNoteFromAnywhere();
            }
            // Ctrl+B (or Cmd+B): toggle the PDF's bookmark sidebar. Unlike the hotkeys
            // above this one does NOT reach across the app — see togglePdfBookmarkSidebar.
            if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey &&
                (e.key === 'b' || e.key === 'B')) {
                if (this.togglePdfBookmarkSidebar()) {
                    e.preventDefault();
                }
            }
            // Any other key nothing else wants goes to the PDF reader while it is on screen
            this.routeUnfocusedKeyToPdfReader(e);
        });

        // Where the user last pressed or moved the focus, for routeUnfocusedKeyToPdfReader. Capture
        // phase, so a handler that stops propagation cannot hide a press from it.
        const noteInteraction = (e) => {
            this.lastInteractionInSidebarPanes = this.isInSidebarPanes(e.target);
        };
        document.addEventListener('pointerdown', noteInteraction, true);
        document.addEventListener('focusin', noteInteraction, true);
    }


    /**
     * Passes a key to the PDF reader when the reader tab is the one on screen and the key is not
     * meant for anything else. The reader's own listener only hears keys while the focus is inside
     * it, and the focus often is not: it drops to the page body when a dialog closes (Ctrl+G, the
     * highlights list) or when the focused element is hidden (the map, as the Case Book tab opens),
     * and it stays on a button that was clicked, so PageUp/PageDown "sometimes" did nothing.
     * Keys stay where they are when a dialog is open, when the focus is in a field (or in the reader
     * itself, whose own listener has them), and when the user is working in the sidebar's search
     * panes: focused there, or, with nothing focused, having last clicked there (the browser then
     * scrolls the list clicked last).
     * @param {KeyboardEvent} e
     */
    routeUnfocusedKeyToPdfReader(e) {
        if (!this.pdfViewer || e.defaultPrevented || e.isComposing) return;
        if (document.body.classList.contains('modal-open')) return;
        if (!this.isMainPanelReadingTabActive()) return;

        const target = e.target;
        if (!(target instanceof Element)) return;
        if (target.isContentEditable || target.closest('input, textarea, select')) return;
        if (this.isInSidebarPanes(target)) return;
        const nothingFocused = (target === document.body || target === document.documentElement);
        if (nothingFocused && this.lastInteractionInSidebarPanes) return;

        this.pdfViewer.handleKeyFromOutside(e);
    }


    /**
     * Whether an element is in the sidebar's search panes (its tabs, search boxes, result lists and
     * play log), as opposed to the sidebar's frame (the collapse and full-width buttons, the resize
     * handle), which uses no keys of its own.
     * @param {EventTarget} el
     * @returns {boolean}
     */
    isInSidebarPanes(el) {
        return (el instanceof Element) && !!el.closest(`#${ID_SIDEBAR} .sidebar-content`);
    }


    /**
     * Toggles the PDF bookmark sidebar (Ctrl+B), but only while the reader tab is the
     * one on screen. The other global PDF hotkeys open the reader on the user's behalf;
     * this one deliberately does not — a table of contents is a thing you reach for
     * while reading, and stealing Ctrl+B from the browser out on the map would cost
     * more than it gives.
     * @returns {boolean} True if the hotkey was consumed (so the caller can decide
     *   whether to preventDefault, leaving Ctrl+B to the browser otherwise).
     */
    togglePdfBookmarkSidebar() {
        if (!this.isMainPanelReadingTabActive()) return false;

        const pdfViewer = this.getPdfViewer();
        if (!pdfViewer || !pdfViewer.hasPdf()) return false;

        pdfViewer.toggleBookmarkSidebar();
        return true;
    }


    /**
     * Opens the PDF "go to lead" dialog from anywhere in the app (Ctrl+G).
     * Ensures the reading pane is visible and a PDF is loaded, switches to the
     * reading tab so the resulting navigation is visible, then shows the dialog.
     */
    async showPdfLeadGoToDialogFromAnywhere() {
        const pdfViewer = this.getPdfViewer();
        if (!pdfViewer) return;

        // Make sure the reading pane is showing and a PDF is loaded.
        this.ensurePdfViewerVisible();
        await this.loadLastPdfIfNothingLoaded(false);
        if (!pdfViewer.hasPdf()) {
            showStatusWarning('Load a PDF first to go to a lead.');
            return;
        }

        // Switch to the reading tab if needed so the goto navigation is visible.
        if (!this.isMainPanelReadingTabActive()) {
            await this.switchToMainPanelTabByIdAsync(ID_MAIN_READING_TAB);
            this.ensurePdfViewerInitialized();
            await pdfViewer.adjustToViewerSizeChanges();
        }

        await pdfViewer.showLeadGoToDialog();
    }


    /**
     * Opens the highlights dialog in "add a note" mode from anywhere in the app (Ctrl+L).
     * Ensures the reading pane is visible and a PDF is loaded, switches to the reading tab so
     * the note is pinned to the page the user is reading, then opens the dialog which adds and
     * focuses a fresh editable note.
     */
    async showPdfAddNoteFromAnywhere() {
        const pdfViewer = this.getPdfViewer();
        if (!pdfViewer) return;

        // Make sure the reading pane is showing and a PDF is loaded.
        this.ensurePdfViewerVisible();
        await this.loadLastPdfIfNothingLoaded(false);
        if (!pdfViewer.hasPdf()) {
            showStatusWarning('Load a PDF first to add a note.');
            return;
        }

        // Switch to the reading tab if needed so the note is pinned to the visible page.
        if (!this.isMainPanelReadingTabActive()) {
            await this.switchToMainPanelTabByIdAsync(ID_MAIN_READING_TAB);
            this.ensurePdfViewerInitialized();
            await pdfViewer.adjustToViewerSizeChanges();
        }

        pdfViewer.showHighlightsListDialog({ autoAddNote: true });
    }

    async loadLastPdfIfNothingLoaded(flagBrowseForPdfIfNoLast, flagSkipRestorePage = false) {
        const pdfViewer = this.getPdfViewer();
        if (!pdfViewer) return;

        // Check if nothing is currently loaded
        if (pdfViewer.hasPdf()) return;

        // Get the last PDF URL from state manager
        const lastUrl = this.stateManager.getLastPdfUrl();
        if (lastUrl) {
            // flagSkipRestorePage: caller will jump to a specific lead/location after load, so don't
            // first restore (and flash) the saved last-viewed page.
            // (logged only when it really loaded, see loadPdfInReadingPane)
            if (await pdfViewer.loadPdf(lastUrl, LEADS_SECTION_LABELS, flagSkipRestorePage)) {
                this.logPlayPdfLoaded(lastUrl);
            }
        } else {
            if (flagBrowseForPdfIfNoLast) {
                // ask user to load pdf
                await pdfViewer.showLoadDialog();
            }
        }
    }



    isLogPlayPanelEnabled() {
        return !(this.stateManager.getHideLogPanel());
    }

    logPlayPdfLoaded(pdfPath) {
        if (!this.isLogPlayPanelEnabled()) {
            return;
        }
        this.getPlayLogger().addLogText(`PDF loaded "${pdfPath}"`);
    }

    logPlaySearchRun(searchPack) {
        if (!this.isLogPlayPanelEnabled()) {
            return;
        }
        const searchType = searchPack.searchType;
        const query = searchPack.query;
        const label = `${searchType} "${query}"`;
        this.getPlayLogger().addLogText(`Ran search: ${label}.`);
    }


    logPlayClickedPoi(item) {
        if (!this.isLogPlayPanelEnabled()) {
            return;
        }
        const ptype = item.ptype;
        if (ptype == PTYPES.SEARCH) {
            return this.logPlaySearchRun(item);
        }

        const label = this.calcPoiItemLabel(item);
        this.getPlayLogger().addLogText(`Clicked poi: ${label}.`);
    }

    logPlayReadLead(item, leadId, flagFound) {
        if (!this.isLogPlayPanelEnabled()) {
            return;
        }
        const label = this.calcPoiItemLabel(item);
        if (flagFound) {
            this.getPlayLogger().addLogText(`* Read lead: ${label}.`);
        } else {
            this.getPlayLogger().addLogText(`* N/A lead: ${label}.`);
        }
    }

    /**
     * Looks a lead up for a play log line, or returns null when no world is loaded. The PDF viewer is
     * app-scoped and usable with no world (after a world config failed to load, or mid world-switch
     * between resetOwnedStuff() and loadPoiDataSources), so its log callbacks then log the raw label.
     * @param {string} lead - The lead (bookmark label or typed lead)
     * @returns {Object|null} The POI item, or null
     */
    findPoiItemByLeadForPlayLog(lead) {
        if (!this.isWorldReady() || !this.poiDataManager) {
            return null;
        }
        return this.poiDataManager.findItemByLead(lead);
    }


    logPlayPdfBookmarkClick(label, pageNumber) {
        if (!this.isLogPlayPanelEnabled()) {
            return;
        }
        // let's TRY to lookup lead by lead
        const item = this.findPoiItemByLeadForPlayLog(label);
        if (item) {
            label = this.calcPoiItemLabel(item);
        }
        this.getPlayLogger().addLogText(`* PDF click: ${label} on p.${pageNumber}.`);
    }


    logPlayPdfLeadGoTo(leadInput, flagFound) {
        if (!this.isLogPlayPanelEnabled()) {
            return;
        }
        let label = leadInput;
        const item = this.findPoiItemByLeadForPlayLog(leadInput);
        if (item) {
            label = this.calcPoiItemLabel(item);
        }
        if (flagFound) {
            this.getPlayLogger().addLogText(`* PDF goto: ${label}.`);
        } else {
            this.getPlayLogger().addLogText(`* PDF goto N/A: ${label}.`);
        }
    }


    calcPoiItemLabel(item) {
        const dName = item.dName;
        const lead = item.lead;
        const location = item.jregion ? `at ${item.jregion}-${item.loclabel}` : ``;
        const label = `${dName} (${lead}) ${location}`;
        return label;
    }





    externalGeoLocate1940sNyc(uid, item) {
        if (!this.isItemInNewYorkCity(item)) {
            showStatusWarning('1940s.nyc covers New York City only, and this place is outside it.');
            return;
        }
        const coords = this.getItemLatLon(item);
        if (!coords) return;
        const { latitude, longitude } = coords;
        const zoom = 18;
        const angle = 29;
        const url = WEB_URLS.EXTERNAL_GEOLOC_1940sNYC
            .replace('${zoomLevel}', zoom)
            .replace('${latitude}', latitude)
            .replace('${longitude}', longitude)
            .replace('${rotation}', angle);
        window.open(url, '_blank');
    }


    externalGeoLocateGoogleMaps(uid, item) {
        const coords = this.getItemLatLon(item);
        if (!coords) return;
        const { latitude, longitude } = coords;
        const zoom = 18;
        const url = WEB_URLS.EXTERNAL_GEOLOC_GOOGLE_MAPS
            .replace('${zoomLevel}', zoom)
            .replace('${latitude}', latitude)
            .replace('${longitude}', longitude);
        window.open(url, '_blank');
        //console.log(`Opening Google Maps at ${latitude},${longitude}: "${url}".`);
    }


    externalGeoLocateArcgisAerial(uid, item) {
        const coords = this.getItemLatLon(item);
        if (!coords) return;
        const { latitude, longitude } = coords;
        const zoom = 18;
        const url = WEB_URLS.EXTERNAL_GEOLOC_ARCGIS_AERIAL
            .replace('${zoomLevel}', zoom)
            .replace('${latitude}', latitude)
            .replace('${longitude}', longitude);
        window.open(url, '_blank');
    }


    /**
     * Whether the external geolocate actions (1940s.nyc, Google Maps, ArcGIS) make sense for an item:
     * only on a GEOGRAPHIC world (a raster world's coordinates are image pixels, which read as a spot
     * near latitude 0, longitude 0) and only for an item with a Point (a polygon's coordinates are
     * rings, which made a url with "undefined" in it). The Lister offers the extra-actions button only
     * when this is true, and getItemLatLon refuses otherwise.
     * @param {Object} item - The result item
     * @returns {boolean}
     */
    canGeolocateItemExternally(item) {
        return !!(this.mapManager && !this.mapManager.isRasterMode() && itemHasPointGeometry(item));
    }


    /**
     * Whether an item's place lies inside New York City (NYC_LONLAT_BOUNDS), the only area 1940s.nyc
     * covers: so its geolocate action is never offered on another world (SHCD's London) nor for an
     * off-map place of the NYC world (decision D9, Oct 2026). Silent: false for anything that cannot
     * be geolocated at all (canGeolocateItemExternally).
     * @param {Object} item - The result item
     * @returns {boolean}
     */
    isItemInNewYorkCity(item) {
        if (!this.canGeolocateItemExternally(item)) {
            return false;
        }
        const [longitude, latitude] = transformGeometry(item.geometry, 'EPSG:3857', 'EPSG:4326').coordinates;
        const [minLon, minLat, maxLon, maxLat] = NYC_LONLAT_BOUNDS;
        return (longitude >= minLon) && (longitude <= maxLon) && (latitude >= minLat) && (latitude <= maxLat);
    }


    getItemLatLon(item) {
        if (!this.canGeolocateItemExternally(item)) {
            showStatusWarning('This item has no map location that can be looked up on an outside map.');
            return null;
        }
        const fromProjection = 'EPSG:3857';
        const toProjection = 'EPSG:4326';
        const externalGeometry = transformGeometry(item.geometry, fromProjection, toProjection);
        const coords = externalGeometry.coordinates;
        return { latitude: coords[1], longitude: coords[0] };
    }




    // DEBUG HELPERS


    debugLabelAllUniqueLocLabelsOnMap() {
        // helper function to show labeled address points on map; this is useful for debugging adding a new game world like Mythos Tales, SHCD, BOI, etc.
        // label every unique jregion+loclabel

        let mapLabels = {};

        // iterate all poi
        let dataCombined = this.poiDataManager.getDataCombined();
        // 1. Loop through all dataCombined elements, collect unique jregion+loclabel entries
        for (const item of dataCombined) {
            if (!item.geometry || !item.geometry.coordinates) continue;
            const jregion = item.jregion || '';
            const loclabel = item.loclabel || '';
            const mapLabel = `${jregion}${loclabel}`;
            if (!mapLabel) continue;
            if (!mapLabels[mapLabel]) {
                mapLabels[mapLabel] = item.geometry.coordinates;
            }
        }

        // 2. Label each unique mapLabel on the map
        for (const [mapLabel, coordinates] of Object.entries(mapLabels)) {
            this.mapManager.addDebugLabel(mapLabel, coordinates);
        }

    }


}

